import React, { useState, useEffect, useMemo } from 'react';
import { PageHeader } from '@/components/Layout';
import { DimensionInput } from '@/components/DimensionInput';
import { useAuth } from '@/context/AuthContext';
import { freightApi, paymentApi, authorizeApi, helcimApi, fabricSettingsApi, inventoryApi, parcelApi, authApi, ordersApi, partsOrdersApi } from '@/services/api';
import clsx from 'clsx';
import { ShippingDisclaimer } from '@/components/ShippingDisclaimer';
import CardFeeConsentModal from '@/components/CardFeeConsentModal';

// ============================================================================
// TYPES
// ============================================================================

interface UChannelItem {
  id: string;
  color: string;
  lengthInches: string;
  quantity: number;
  exactLength: boolean; // true = exact length, false = factory default (+6 inches)
}

interface CartItem {
  id: string;
  category: 'uchannel' | 'accessory' | 'part' | 'fabric';
  name: string;
  description?: string;
  sku?: string;
  color?: string;
  quantity: number;
  unitPrice: number;
  msrpPrice: number;
  unit?: string;
  lengthInches?: number;
  exactLength?: boolean;
  isBackorder?: boolean;
  fabricType?: string;
  fabricColor?: string;
  fabricOrderType?: string;
  width?: number;
  height?: number;
}

interface FreightInfo {
  carrier: string;
  service: string;
  transitTime?: string;
  rate: number;
  fallback?: boolean;
  freightviewShipmentId?: string;
  selectedQuoteId?: string;
  selectedCarrierCode?: string;
}

interface ParcelQuote {
  carrier: string;
  serviceCode: string;
  serviceName: string;
  // No baseAmount / markupPercent / markupAmount: the API strips carrier cost
  // for dealers on purpose. Do not add them back.
  totalAmount: number;
  transitDays: number;
  transitDescription: string;
  guaranteedDelivery?: boolean;
}

// ============================================================================
// PRODUCT DATA
// ============================================================================

const UCHANNEL_COLORS = [
  { value: 'black', label: 'Black' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'white', label: 'White' },
  { value: 'tan', label: 'Tan' },
];

// U-channel dealer price per foot. Kept in sync with the order form / Price
// Builder (UCHANNEL_RATES.DEALER = 9.16) so a dealer pays the same for
// u-channel whether they order it on a screen or buy it standalone here.
const UCHANNEL_PRICING = {
  msrp: 12.75,
  dealer: 9.16,
};

// Accessory component types that show in the "Accessories" section (remotes, switches, sensors)
const ACCESSORY_TYPES = ['Remote', 'Wall Switch', 'Sensor'];

// Sales kit component type → category mapping
const SALES_KIT_CATEGORY_MAP: Record<string, string> = {
  'Sample Kit': 'sample',
  'Marketing': 'marketing',
  'Technical': 'technical',
  'Starter Kit': 'kit',
  'Trade Show': 'kit',
};

// Demo unit component type → category mapping
const DEMO_CATEGORY_MAP: Record<string, string> = {
  'Demo': 'demo',
  'Demo Accessory': 'accessory',
};

// Item type used for both Sales Kit and Demo Units (from API)
interface CatalogItem {
  id: string;
  category: string;
  name: string;
  sku: string;
  msrp: number;
  dealer: number;
  description: string;
  stock: number;
  featured?: boolean;
  imageUrl?: string;
}

// Stock status helper
const getStockStatus = (stock: number | null) => {
  if (stock === null) return { inStock: true, label: null, canOrder: true };
  if (stock === 0) return { inStock: false, label: 'Out of Stock - Order to Get on List', canOrder: true };
  if (stock <= 3) return { inStock: true, label: `Only ${stock} left!`, canOrder: true };
  return { inStock: true, label: `${stock} in stock`, canOrder: true };
};

// Mapped accessory/part item from API
interface AccessoryItem {
  id: string;
  category: string;
  brand: string;
  name: string;
  sku: string;
  msrp: number;
  dealer: number;
  unit?: string;
}

// Fabric ordering data fetched from API (inventory_items + fabric_seam_rules)
interface FabricProduct {
  fabricType: string;
  category: string;
  bomPerLinearInch: number;
  unitCost: number;
  stockLength: number;
  rollWidth: number;
  maxPanelCut: number;
  colors: string[];
}

interface AssemblyParts {
  kederSpline6mm: { costPerInch: number; stockLength: number; unitCost: number; cutFormula: string };
  kederSpline4mm: { costPerInch: number; stockLength: number; unitCost: number; cutFormula: string };
  cornerPatches: { costPerScreen: number };
  strataglass: { unitCost: number };
}

type FabricOrderType = 'fabric_only' | 'panel_replacement';

interface FabricLine {
  id: string;
  width: string;
  height: string;
  qty: number;
  windowPanel: boolean;
  windowCount: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PartsPage() {
  const { user } = useAuth();

  // Check if coming from order form
  const [fromOrder, setFromOrder] = useState(false);
  const [linkedOrderData, setLinkedOrderData] = useState<any>(null);

  // Load linked order data on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFromOrder = urlParams.get('fromOrder') === 'true';
    setFromOrder(isFromOrder);

    if (isFromOrder) {
      const savedData = localStorage.getItem('pendingOrderAccessories');
      if (savedData) {
        try {
          const data = JSON.parse(savedData);
          // Check if data is still fresh (within 2 hours)
          if (data.timestamp && Date.now() - data.timestamp < 2 * 60 * 60 * 1000) {
            setLinkedOrderData(data);
          }
        } catch (e) {
          console.error('Failed to parse linked order data:', e);
        }
      }
    }
  }, []);

  // Return to order with cart items (or without if they changed their mind)
  const returnToOrder = () => {
    if (!linkedOrderData) return;

    if (cart.length > 0) {
      // Save cart items to be picked up by the order form
      const accessoriesForOrder = cart.map(item => ({
        id: item.id,
        name: item.name,
        sku: item.sku,
        category: item.category,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        msrpPrice: item.msrpPrice,
        color: item.color,
        lengthInches: item.lengthInches,
        // Carry the fabric size through to the order form. Fabric lines keep their
        // dimensions in `description` ('234" W × 120" H (seam)') + width/height;
        // dropping these here was why fabric-only accessories lost their size on
        // the invoice/detail (the order-create step has nothing to combine).
        description: item.description,
        width: item.width,
        height: item.height,
      }));

      localStorage.setItem('orderAccessoriesCart', JSON.stringify({
        items: accessoriesForOrder,
        orderId: linkedOrderData.orderId,
        timestamp: Date.now(),
      }));
    }
    // If cart is empty, don't save anything - just go back

    // Don't remove pendingOrderAccessories — NewOrderPage needs it to restore form state

    // Navigate back to order
    window.location.href = linkedOrderData.returnUrl || '/orders/new';
  };

  // Get dealer discount from profile
  const dealerDiscount = useMemo(() => {
    const u = user as any;
    return u?.discountPercent || u?.discount_percent || u?.discountRate || 0;
  }, [user]);

  // Get reward tier display name
  const rewardTierDisplay = useMemo(() => {
    const u = user as any;
    return u?.rewardTierDisplay || u?.reward_tier_display || '';
  }, [user]);

  // Fraction input mode
  const [fractionMode, setFractionMode] = useState(() => {
    return localStorage.getItem('fractionMode') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('fractionMode', fractionMode ? 'true' : 'false');
  }, [fractionMode]);

  // Cart state
  const [cart, setCart] = useState<CartItem[]>([]);

  // U-Channel form - NOW SUPPORTS MULTIPLE LINE ITEMS
  const [uChannelItems, setUChannelItems] = useState<UChannelItem[]>([
    { id: '1', color: 'black', lengthInches: '', quantity: 1, exactLength: false },
  ]);

  // Accessory quantities
  const [accessoryQty, setAccessoryQty] = useState<Record<string, number>>({});

  // Sales Kit quantities
  const [salesKitQty, setSalesKitQty] = useState<Record<string, number>>({});

  // Demo Unit quantities
  const [demoQty, setDemoQty] = useState<Record<string, number>>({});

  // Parts search and quantities
  const [partsSearch, setPartsSearch] = useState('');
  const [partsQty, setPartsQty] = useState<Record<string, number>>({});

  // Fabric state
  const [fabricProducts, setFabricProducts] = useState<FabricProduct[]>([]);
  const [assemblyParts, setAssemblyParts] = useState<AssemblyParts | null>(null);
  const [fabricOrderType, setFabricOrderType] = useState<FabricOrderType>('panel_replacement');
  const [fabricType, setFabricType] = useState('');
  const [fabricColor, setFabricColor] = useState('');
  const [fabricLines, setFabricLines] = useState<FabricLine[]>([
    { id: '1', width: '', height: '', qty: 1, windowPanel: false, windowCount: 1 },
  ]);

  // Shipping
  const [shippingMethod, setShippingMethod] = useState<'ship' | 'willcall'>('ship');
  const [sidemark, setSidemark] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editingOrderNumber, setEditingOrderNumber] = useState<string | null>(null);
  const [shipStreet, setShipStreet] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');
  const [liftgate, setLiftgate] = useState(false);
  const [residential, setResidential] = useState(false);

  // Freight
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightInfo, setFreightInfo] = useState<FreightInfo | null>(null);
  const [freightQuotes, setFreightQuotes] = useState<Array<{
    quoteId: string;
    carrierName: string;
    carrierCode: string;
    serviceDescription: string;
    totalAmount: number;
    transitTime: string;
  }>>([]);
  const [freightError, setFreightError] = useState('');

  // Parcel (small package) shipping
  const [carrierType, setCarrierType] = useState<'small_package' | 'ltl_freight' | 'mixed' | null>(null);
  const [parcelQuotes, setParcelQuotes] = useState<ParcelQuote[]>([]);
  const [selectedParcelQuote, setSelectedParcelQuote] = useState<ParcelQuote | null>(null);
  const [poBoxWarning, setPoBoxWarning] = useState(false);

  // Checkout
  const [processing, setProcessing] = useState(false);
  const [showInvoiceConfirm, setShowInvoiceConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [achDiscountPercent, setAchDiscountPercent] = useState<number>(0);
  const [authorizeAchDiscountPercent, setAuthorizeAchDiscountPercent] = useState<number>(0);
  const [helcimAchDiscountPercent, setHelcimAchDiscountPercent] = useState<number>(0);

  // Card fee consent — shown before redirecting to the hosted Stripe checkout.
  const [cardConsentOpen, setCardConsentOpen] = useState(false);

  // Authorize.net card payment modal
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardModalTab, setCardModalTab] = useState<'card' | 'saved' | 'bank'>('card');
  const [cardProcessing, setCardProcessing] = useState(false);
  const [cardForm, setCardForm] = useState({ cardNumber: '', expMonth: '', expYear: '', cvv: '', saveCard: false });
  const [helcimCheckoutToken, setHelcimCheckoutToken] = useState<string | null>(null);
  const [helcimSecretToken, setHelcimSecretToken] = useState<string | null>(null);
  const [savedMethods, setSavedMethods] = useState<any[]>([]);

  // ACH address collection (when dealer has no address on file)
  const [achAddressForm, setAchAddressForm] = useState({ street: '', city: '', state: '', zip: '' });
  const hasAddress = !!(user as any)?.addressStreet || !!(user as any)?.address_street || !!(user as any)?.addressZip || !!(user as any)?.address_zip;
  const [selectedSavedMethod, setSelectedSavedMethod] = useState<string | null>(null);
  const [loadingSavedMethods, setLoadingSavedMethods] = useState(false);

  // Fetch ACH discount settings
  useEffect(() => {
    paymentApi.getAchSettings()
      .then(settings => {
        setAchDiscountPercent(settings.achDiscountPercent);
        setAuthorizeAchDiscountPercent((settings as any).authorizeAchDiscountPercent ?? 0);
        setHelcimAchDiscountPercent((settings as any).helcimAchDiscountPercent ?? 0);
      })
      .catch(err => console.error('Failed to fetch ACH settings:', err));
  }, []);


  // Fetch fabric ordering data (pricing + roll widths + colors from inventory)
  useEffect(() => {
    fabricSettingsApi.getFabricOrdering()
      .then(data => {
        setFabricProducts(data.fabrics);
        setAssemblyParts(data.assemblyParts);
      })
      .catch(err => console.error('Failed to fetch fabric ordering data:', err));
  }, []);

  // Fetch accessories & replacement parts from inventory API
  const [apiAccessories, setApiAccessories] = useState<AccessoryItem[]>([]);
  const [apiParts, setApiParts] = useState<AccessoryItem[]>([]);
  const [apiSalesKit, setApiSalesKit] = useState<CatalogItem[]>([]);
  const [apiDemoUnits, setApiDemoUnits] = useState<CatalogItem[]>([]);
  useEffect(() => {
    inventoryApi.getAccessories()
      .then(items => {
        const mapped: AccessoryItem[] = items.map((item: any) => {
          const descLower = (item.description || '').toLowerCase();
          let unit: string | undefined;
          if (descLower.includes('per inch')) unit = 'per inch';
          else if (descLower.includes('per ft') || descLower.includes('per foot')) unit = 'per ft';
          // Dealer price = the per-item dealer_price set on the factory
          // inventory page. Blank/0 → 0, which the UI shows as "price on
          // request" (NOT the old cost×5 formula, which produced nonsense like
          // $1,000+ motors and dealer > MSRP inversions).
          const dealer = item.dealerPrice && item.dealerPrice > 0 ? item.dealerPrice : 0;
          return {
            id: item.id,
            category: (item.componentType || '').toLowerCase(),
            brand: item.supplierName || '',
            name: item.description || '',
            sku: item.partNumber || '',
            msrp: item.unitPrice || 0,
            dealer,
            unit,
          };
        });
        setApiAccessories(mapped.filter(a => ACCESSORY_TYPES.some(t => t.toLowerCase() === a.category)));
        setApiParts(mapped.filter(a => !ACCESSORY_TYPES.some(t => t.toLowerCase() === a.category)));
      })
      .catch(err => console.error('Failed to fetch accessories:', err));

    inventoryApi.getSalesKit()
      .then(items => {
        setApiSalesKit(items.map((item: any) => ({
          id: item.id,
          category: SALES_KIT_CATEGORY_MAP[item.componentType] || 'other',
          name: item.description || '',
          sku: item.partNumber || '',
          msrp: item.unitPrice || 0,
          dealer: item.dealerPrice && item.dealerPrice > 0 ? item.dealerPrice : 0,
          description: item.notes || '',
          // 42001 (Omega Pro swatch kit) is unlocked as a FREE, made-to-order
          // sample — we source them free and only charge UPS shipping. null stock
          // = always available (no "back order"). The other swatch kits stay locked.
          stock: item.partNumber === '42001' ? null : (item.quantityOnHand ?? 0),
          featured: item.componentType === 'Starter Kit' || item.partNumber === '42001',
          imageUrl: item.imageFilename || undefined,
        })));
      })
      .catch(err => console.error('Failed to fetch sales kit:', err));

    inventoryApi.getDemoUnits()
      .then(items => {
        setApiDemoUnits(items.map((item: any) => {
          const desc = (item.description || '').toLowerCase();
          let category = DEMO_CATEGORY_MAP[item.componentType] || 'demo';
          // Derive subcategory from description for demo screens
          if (category === 'demo') {
            if (desc.includes('tabletop')) category = 'tabletop';
            else if (desc.includes('freestanding')) category = 'freestanding';
            else category = 'showroom';
          }
          return {
            id: item.id,
            category,
            name: item.description || '',
            sku: item.partNumber || '',
            msrp: item.unitPrice || 0,
            dealer: item.unitCost || 0,
            description: item.notes || '',
            stock: item.quantityOnHand ?? 0,
            featured: (item.description || '').toLowerCase().includes('premium'),
          };
        }));
      })
      .catch(err => console.error('Failed to fetch demo units:', err));
  }, []);

  // Load saved shipping preference and prefill from profile
  useEffect(() => {
    if (user) {
      const u = user as any;

      // Load saved shipping preference from localStorage
      const savedPrefs = localStorage.getItem('partsShippingPrefs');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          // Set shipping method from saved preference
          if (prefs.shippingMethod === 'willcall' || prefs.shippingMethod === 'ship') {
            setShippingMethod(prefs.shippingMethod);
          }
          // If they previously used shipping, load their saved address (prefer saved over profile)
          if (prefs.shippingMethod === 'ship' && prefs.address) {
            setShipStreet(prefs.address.street || u.addressStreet || u.address_street || '');
            setShipCity(prefs.address.city || u.addressCity || u.address_city || '');
            setShipState(prefs.address.state || u.addressState || u.address_state || '');
            setShipZip((prefs.address.zip || u.addressZip || u.address_zip || '').replace(/-.*$/, '').slice(0, 5));
            if (prefs.address.liftgate !== undefined) setLiftgate(prefs.address.liftgate);
            if (prefs.address.residential !== undefined) setResidential(prefs.address.residential);
          } else {
            // No saved address, use profile
            setShipStreet(u.addressStreet || u.address_street || '');
            setShipCity(u.addressCity || u.address_city || '');
            setShipState(u.addressState || u.address_state || '');
            setShipZip((u.addressZip || u.address_zip || '').replace(/-.*$/, '').slice(0, 5));
          }
        } catch (e) {
          // Invalid saved prefs, use defaults from profile
          setShipStreet(u.addressStreet || u.address_street || '');
          setShipCity(u.addressCity || u.address_city || '');
          setShipState(u.addressState || u.address_state || '');
          setShipZip((u.addressZip || u.address_zip || '').replace(/-.*$/, '').slice(0, 5));
        }
      } else {
        // No saved prefs, use profile defaults
        setShipStreet(u.addressStreet || u.address_street || '');
        setShipCity(u.addressCity || u.address_city || '');
        setShipState(u.addressState || u.address_state || '');
        setShipZip((u.addressZip || u.address_zip || '').replace(/-.*$/, '').slice(0, 5));
      }
    }
  }, [user]);

  // Load parts order data from sessionStorage when editing
  useEffect(() => {
    const editDataStr = sessionStorage.getItem('editPartsOrderData');
    if (editDataStr) {
      try {
        const editData = JSON.parse(editDataStr);
        console.log('[PartsPage] Loading edit data:', editData);
        console.log('[PartsPage] Items from edit data:', editData.items);
        console.log('[PartsPage] Items count:', editData.items?.length || 0);

        // Track that we're editing an existing order — checkout/submit handlers
        // will PATCH this order in place instead of creating a new one.
        if (editData.orderId) {
          setEditingOrderId(editData.orderId);
          setEditingOrderNumber(editData.orderNumber || null);
        }

        // Convert saved order items to cart format
        if (editData.items && Array.isArray(editData.items)) {
          const cartItems: CartItem[] = editData.items.map((item: any, index: number) => {
            console.log(`[PartsPage] Processing item ${index}:`, item);

            // Detect item type from various possible fields
            const itemType = item.type || item.category || item.productType || item.product_type || 'part';
            const itemName = item.name || item.description || 'Unknown Item';

            // Handle U-channel items
            if (itemType === 'uchannel' || itemType === 'u-channel' || itemName.toLowerCase().includes('u-channel')) {
              const cartItem = {
                id: item.id || `uchannel-${index}-${Date.now()}`,
                category: 'uchannel' as const,
                name: item.name || 'U-Channel',
                description: item.description,
                color: item.color || item.fabricColor || item.fabric_color || 'black',
                quantity: item.quantity || 1,
                unitPrice: item.unitPrice || item.unit_price || item.totalPrice || item.total_price || 0,
                msrpPrice: item.msrpPrice || item.msrp_price || 0,
                unit: item.unit || 'ea',
                lengthInches: item.lengthInches || item.length_inches || item.heightLeft || item.height_left,
                exactLength: item.exactLength || item.exact_length || false,
              };
              console.log(`[PartsPage] Mapped U-channel item:`, cartItem);
              return cartItem;
            }

            // Handle accessory items (remotes, sensors, etc.)
            if (itemType === 'accessory' || itemType === 'remote' || itemType === 'switch' || itemType === 'sensor') {
              const cartItem = {
                id: item.id || item.sku || `accessory-${index}`,
                category: 'accessory' as const,
                name: itemName,
                sku: item.sku,
                quantity: item.quantity || 1,
                unitPrice: item.unitPrice || item.unit_price || item.totalPrice || item.total_price || 0,
                msrpPrice: item.msrpPrice || item.msrp_price || 0,
              };
              console.log(`[PartsPage] Mapped accessory item:`, cartItem);
              return cartItem;
            }

            // Handle generic part items
            const cartItem: CartItem = {
              id: item.id || item.sku || `part-${index}`,
              category: 'part' as const,
              name: itemName,
              sku: item.sku,
              quantity: item.quantity || 1,
              unitPrice: item.unitPrice || item.unit_price || item.totalPrice || item.total_price || 0,
              msrpPrice: item.msrpPrice || item.msrp_price || 0,
              unit: item.unit,
            };
            console.log(`[PartsPage] Mapped part item:`, cartItem);
            return cartItem;
          });

          console.log('[PartsPage] Final cart items:', cartItems);
          setCart(cartItems);
        }

        // Load shipping info if available
        const order = editData.order;
        if (order) {
          console.log('[PartsPage] Loading order shipping info:', order);

          // Try different formats for shipping address
          let shippingAddr = order.shippingAddress || order.shipping_address || order.shipToAddress || order.ship_to_address;

          // Handle if it's a string (JSON)
          if (typeof shippingAddr === 'string') {
            try {
              shippingAddr = JSON.parse(shippingAddr);
            } catch (e) {
              console.warn('[PartsPage] Could not parse shipping address:', shippingAddr);
              shippingAddr = null;
            }
          }

          if (shippingAddr && typeof shippingAddr === 'object') {
            console.log('[PartsPage] Shipping address:', shippingAddr);
            setShipStreet(shippingAddr.street || shippingAddr.address || '');
            setShipCity(shippingAddr.city || '');
            setShipState(shippingAddr.state || '');
            setShipZip(shippingAddr.zip || shippingAddr.zipCode || shippingAddr.zip_code || '');
          }

          const shippingMeth = order.shippingMethod || order.shipping_method;
          console.log('[PartsPage] Shipping method:', shippingMeth);
          if (shippingMeth === 'willcall' || shippingMeth === 'will_call') {
            setShippingMethod('willcall');
          } else {
            setShippingMethod('ship');
          }

          // Pre-fill sidemark + PO from existing order
          setSidemark(order.sidemark || '');
          setPoNumber(order.poNumber || order.po_number || '');
        }

        // Clear the sessionStorage after loading
        sessionStorage.removeItem('editPartsOrderData');
      } catch (err) {
        console.error('[PartsPage] Error loading edit data:', err);
        sessionStorage.removeItem('editPartsOrderData');
      }
    }
  }, []);

  // ============================================================================
  // U-CHANNEL HELPERS
  // ============================================================================

  const addUChannelLine = () => {
    setUChannelItems(prev => [
      ...prev,
      { id: Date.now().toString(), color: 'black', lengthInches: '', quantity: 1, exactLength: false },
    ]);
  };

  const removeUChannelLine = (id: string) => {
    if (uChannelItems.length <= 1) return;
    setUChannelItems(prev => prev.filter(item => item.id !== id));
  };

  const updateUChannelLine = (id: string, field: keyof UChannelItem, value: any) => {
    setUChannelItems(prev =>
      prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  // Calculate U-channel price for a single item (input is inches)
  const calculateUChannelPrice = (lengthInches: number, exactLength: boolean) => {
    // Add 6 inches for factory default
    const actualLengthInches = exactLength ? lengthInches : lengthInches + 6;
    const feet = actualLengthInches / 12;
    return {
      dealerPrice: feet * UCHANNEL_PRICING.dealer,
      msrpPrice: feet * UCHANNEL_PRICING.msrp,
      actualLengthInches,
      feet,
    };
  };

  // Add all U-Channel items to cart
  // ---- Assembly catalogue -------------------------------------------------
  // Buying an assembly outright, with no prior order to read sizes from — so
  // the dealer supplies the length and colour themselves.
  const [asmCatalog, setAsmCatalog] = useState<Array<{ code: string; label: string; lengthLabel: string; hasSide: boolean; needsMotorSide?: boolean; mountOptions?: string | null; maxLengthInches?: number }>>([]);
  const [asmForm, setAsmForm] = useState<Record<string, { length: string; side: string; color: string; qty: number; motorSide: string; mount: string }>>({});
  const [asmQuote, setAsmQuote] = useState<Record<string, { subtotal: number; components: number } | null>>({});
  const [asmBusy, setAsmBusy] = useState<string | null>(null);
  const [asmError, setAsmError] = useState<string | null>(null);

  useEffect(() => {
    partsOrdersApi.getAssemblies()
      .then(list => {
        setAsmCatalog(list);
        setAsmForm(Object.fromEntries(list.map(a => [a.code, {
          length: '', side: 'both', color: 'black', qty: 1,
          motorSide: 'right',
          mount: a.mountOptions === 'track_mount' ? 'face' : (a.mountOptions === 'wall_ceiling' ? 'wall' : ''),
        }])));
      })
      .catch(() => { /* catalogue is optional — the rest of the page still works */ });
  }, []);

  const asmField = (code: string) => asmForm[code] || { length: '', side: 'both', color: 'black', qty: 1 };
  const setAsmField = (code: string, patch: any) =>
    setAsmForm(prev => ({ ...prev, [code]: { ...asmField(code), ...patch } }));

  /** Price server-side so what's quoted is what's charged. */
  const quoteAssembly = async (a: any) => {
    const f = asmField(a.code);
    const len = parseFloat(f.length);
    if (!Number.isFinite(len) || len <= 0) {
      setAsmError(`Enter a ${a.lengthLabel.toLowerCase()} for ${a.label}.`);
      return null;
    }
    // Told here for a quick answer; the server enforces it regardless.
    if (a.maxLengthInches && len > a.maxLengthInches) {
      setAsmError(`${a.label}: ${a.lengthLabel.toLowerCase()} of ${len}" is over the ${a.maxLengthInches}" maximum we build. Call us if you need something larger.`);
      return null;
    }
    setAsmBusy(a.code); setAsmError(null);
    try {
      const q = await partsOrdersApi.price({
        assemblies: [{
          assembly: a.code, lengthInches: len,
          side: a.hasSide ? f.side : undefined,
          motorSide: a.needsMotorSide ? f.motorSide : undefined,
          mount: a.mountOptions ? f.mount : undefined,
          color: f.color, quantity: Math.max(1, f.qty),
        }],
      });
      setAsmQuote(prev => ({ ...prev, [a.code]: { subtotal: q.subtotal, components: q.lines.length } }));
      return q;
    } catch (err: any) {
      setAsmError(err?.response?.data?.message || `Could not price that ${a.label}.`);
      setAsmQuote(prev => ({ ...prev, [a.code]: null }));
      return null;
    } finally {
      setAsmBusy(null);
    }
  };

  const addCatalogAssemblyToCart = async (a: any) => {
    const q = await quoteAssembly(a);
    if (!q) return;
    const f = asmField(a.code);
    const len = parseFloat(f.length);
    const sideLabel = a.hasSide && f.side !== 'both' ? ` (${f.side})` : '';
    const mountLabelMap: Record<string, string> = {
      face: 'Face', inside: 'Inside', inside_face: 'Inside/Face', inside_outside: 'Inside/Outside',
      recessed: 'Recessed', wall: 'Wall', ceiling: 'Ceiling',
    };
    const specLabels = [
      a.needsMotorSide ? `Motor: ${f.motorSide === 'left' ? 'Left' : 'Right'}` : null,
      a.mountOptions ? `Mount: ${mountLabelMap[f.mount] || f.mount}` : null,
    ].filter(Boolean).join(' · ');
    setCart(prev => [...prev, {
      id: `asm-${a.code}-${Date.now()}`,
      category: 'part',
      name: `${a.label}${sideLabel}`,
      description: `${len}" · ${f.color}${specLabels ? ` · ${specLabels}` : ''}`,
      sku: a.code,
      color: f.color,
      quantity: Math.max(1, f.qty),
      // Server-quoted total for the whole request; unit price is per assembly.
      unitPrice: Math.round((q.subtotal / Math.max(1, f.qty)) * 100) / 100,
      msrpPrice: Math.round((q.lines.reduce((t: number, l: any) => t + l.msrpPrice * l.quantity, 0) / Math.max(1, f.qty)) * 100) / 100,
      unit: 'ea',
      lengthInches: len,
      exactLength: true,
    } as CartItem]);
  };

  // ---- Replacement parts from a past order -------------------------------
  // The point is that nobody re-measures: cut lengths come off the order that
  // was actually built, deductions already applied.
  const [replOrderNumber, setReplOrderNumber] = useState('');
  const [replLoading, setReplLoading] = useState(false);
  const [replError, setReplError] = useState<string | null>(null);
  const [replOrder, setReplOrder] = useState<any>(null);
  const [replScreen, setReplScreen] = useState<number | null>(null);
  const [replSide, setReplSide] = useState<Record<string, 'left' | 'right' | 'both'>>({});
  const [replAdding, setReplAdding] = useState<string | null>(null);

  const lookupReplacementOrder = async () => {
    const num = replOrderNumber.trim();
    if (!num) return;
    setReplLoading(true); setReplError(null); setReplOrder(null); setReplScreen(null);
    try {
      const data = await partsOrdersApi.getSourceOrder(num);
      setReplOrder(data);
      if (data.screens?.length === 1) setReplScreen(data.screens[0].itemNumber);
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      setReplError(
        status === 403 ? 'That order belongs to another dealer.'
        : status === 404 ? 'No order found with that number.'
        // A 400 carries a specific reason — e.g. it's a parts order, not a
        // screen order. Show that rather than a generic failure.
        : status === 400 && serverMsg ? serverMsg
        : 'Could not look up that order. Please try again.');
    } finally {
      setReplLoading(false);
    }
  };

  const currentReplScreen = () =>
    replOrder?.screens?.find((s: any) => s.itemNumber === replScreen) || null;

  /** Price an assembly server-side, then add the priced result to the cart. */
  const addAssemblyToCart = async (asm: any) => {
    const screen = currentReplScreen();
    if (!screen) return;
    const key = `${screen.itemNumber}-${asm.code}`;
    setReplAdding(key);
    try {
      const side = asm.needsSide ? (replSide[key] || 'both') : undefined;
      const quote = await partsOrdersApi.price({
        assemblies: [{
          assembly: asm.code,
          lengthInches: asm.lengthInches,
          side,
          color: screen.systemColor || undefined,
          quantity: 1,
          // The fabric panel is copied off this screen rather than rebuilt from
          // a length, so the server needs to know which screen.
          orderItemId: asm.orderItemId,
        }],
      });
      const label = asm.needsSide ? `${asm.label} (${side})` : asm.label;
      // A panel is identified by its fabric and both dimensions; every other
      // assembly is a single cut length in the frame colour.
      const isPanel = asm.code === 'fabric_panel';
      const description = isPanel
        ? [`${screen.width}" × ${screen.height}"`, [screen.fabricType, screen.fabricColor].filter(Boolean).join(' '), `from ${replOrder.orderNumber}`]
            .filter(Boolean).join(' · ')
        : `${asm.lengthInches}" · from ${replOrder.orderNumber}`;
      setCart(prev => [...prev, {
        id: `repl-${key}-${Date.now()}`,
        category: 'part',
        name: `${label} — ${replOrder.orderNumber} screen #${screen.itemNumber}`,
        description,
        sku: asm.code,
        color: (isPanel ? screen.fabricColor : screen.systemColor) || undefined,
        quantity: 1,
        // Server-priced: the dealer is charged what the quote said.
        unitPrice: quote.subtotal,
        msrpPrice: quote.lines.reduce((t: number, l: any) => t + l.msrpPrice * l.quantity, 0),
        unit: 'ea',
        // A panel has two dimensions, so there is no single cut length to carry.
        ...(isPanel ? {} : { lengthInches: asm.lengthInches, exactLength: true }),
      } as CartItem]);
    } catch (err: any) {
      setReplError(err?.response?.data?.message || 'Could not price that assembly.');
    } finally {
      setReplAdding(null);
    }
  };

  /** Add a single part at the cut length it was originally built to. */
  const addReplacementPartToCart = async (part: any) => {
    const screen = currentReplScreen();
    if (!screen) return;
    const key = `${screen.itemNumber}-${part.partNumber}`;
    setReplAdding(key);
    try {
      const quote = await partsOrdersApi.price({
        lines: [{
          partNumber: part.partNumber,
          description: part.description,
          color: screen.systemColor || undefined,
          lengthInches: part.cutLength || undefined,
          quantity: 1,
        }],
      });
      const line = quote.lines[0];
      setCart(prev => [...prev, {
        id: `repl-${key}-${Date.now()}`,
        category: 'part',
        name: part.description || part.partNumber,
        description: `${replOrder.orderNumber} screen #${screen.itemNumber}`,
        sku: part.partNumber,
        color: screen.systemColor || undefined,
        quantity: 1,
        unitPrice: line.dealerPrice,
        msrpPrice: line.msrpPrice,
        unit: 'ea',
        lengthInches: part.cutLength || undefined,
        exactLength: !!part.cutLength,
      } as CartItem]);
    } catch (err: any) {
      setReplError(err?.response?.data?.message || 'Could not price that part.');
    } finally {
      setReplAdding(null);
    }
  };

  const addUChannelToCart = () => {
    const validItems = uChannelItems.filter(item => {
      const length = parseFloat(item.lengthInches);
      return length > 0 && item.quantity > 0;
    });

    if (validItems.length === 0) {
      setError('Please enter valid length and quantity for U-Channel');
      return;
    }

    const newCartItems: CartItem[] = validItems.map(item => {
      const lengthInches = parseFloat(item.lengthInches);
      const { dealerPrice, msrpPrice, actualLengthInches, feet } = calculateUChannelPrice(
        lengthInches,
        item.exactLength
      );
      const colorLabel = UCHANNEL_COLORS.find(c => c.value === item.color)?.label || item.color;

      return {
        id: `uchannel-${item.color}-${item.id}-${Date.now()}`,
        category: 'uchannel' as const,
        name: `U-Channel - ${colorLabel}`,
        description: `${actualLengthInches}" (${feet.toFixed(2)} ft) x ${item.quantity}${!item.exactLength ? ' (Factory +6")' : ' (Exact)'}`,
        color: item.color,
        quantity: item.quantity,
        unitPrice: dealerPrice,
        msrpPrice: msrpPrice,
        unit: 'ea',
        lengthInches: actualLengthInches,
        exactLength: item.exactLength,
      };
    });

    setCart(prev => [...prev, ...newCartItems]);
    // Reset U-channel form
    setUChannelItems([{ id: Date.now().toString(), color: 'black', lengthInches: '', quantity: 1, exactLength: false }]);
    setError('');
  };

  // Add sales kit item to cart
  const addSalesKitToCart = (item: CatalogItem) => {
    const qty = salesKitQty[item.id] || 1;
    if (qty <= 0) return;

    const isBackorder = item.stock !== null && item.stock <= 0;

    const existingIndex = cart.findIndex(c => c.id === item.id);
    if (existingIndex >= 0) {
      setCart(prev => prev.map((c, i) =>
        i === existingIndex ? { ...c, quantity: c.quantity + qty } : c
      ));
    } else {
      const cartItem: CartItem = {
        id: item.id,
        category: 'part',
        name: item.name,
        sku: item.sku,
        quantity: qty,
        unitPrice: item.dealer,
        msrpPrice: item.msrp,
        isBackorder,
      };
      setCart(prev => [...prev, cartItem]);
    }

    setSalesKitQty(prev => ({ ...prev, [item.id]: 1 }));
  };

  // Add demo unit to cart
  const addDemoToCart = (item: CatalogItem) => {
    const qty = demoQty[item.id] || 1;
    if (qty <= 0) return;

    const existingIndex = cart.findIndex(c => c.id === item.id);
    if (existingIndex >= 0) {
      setCart(prev => prev.map((c, i) =>
        i === existingIndex ? { ...c, quantity: c.quantity + qty } : c
      ));
    } else {
      const cartItem: CartItem = {
        id: item.id,
        category: 'part',
        name: item.name,
        sku: item.sku,
        quantity: qty,
        unitPrice: item.dealer,
        msrpPrice: item.msrp,
      };
      setCart(prev => [...prev, cartItem]);
    }

    setDemoQty(prev => ({ ...prev, [item.id]: 1 }));
  };

  // Add accessory to cart
  const addAccessoryToCart = (accessory: AccessoryItem) => {
    const qty = accessoryQty[accessory.id] || 1;
    if (qty <= 0) return;

    const existingIndex = cart.findIndex(item => item.id === accessory.id);
    if (existingIndex >= 0) {
      setCart(prev => prev.map((item, i) =>
        i === existingIndex ? { ...item, quantity: item.quantity + qty } : item
      ));
    } else {
      const item: CartItem = {
        id: accessory.id,
        category: 'accessory',
        name: accessory.name,
        sku: accessory.sku,
        quantity: qty,
        unitPrice: accessory.dealer,
        msrpPrice: accessory.msrp,
      };
      setCart(prev => [...prev, item]);
    }

    setAccessoryQty(prev => ({ ...prev, [accessory.id]: 1 }));
  };

  // Add part to cart
  const addPartToCart = (part: AccessoryItem) => {
    const qty = partsQty[part.id] || 1;
    if (qty <= 0) return;

    const existingIndex = cart.findIndex(item => item.id === part.id);
    if (existingIndex >= 0) {
      setCart(prev => prev.map((item, i) =>
        i === existingIndex ? { ...item, quantity: item.quantity + qty } : item
      ));
    } else {
      const item: CartItem = {
        id: part.id,
        category: 'part',
        name: part.name,
        sku: part.sku,
        quantity: qty,
        unitPrice: part.dealer,
        msrpPrice: part.msrp,
        unit: part.unit,
      };
      setCart(prev => [...prev, item]);
    }

    setPartsQty(prev => ({ ...prev, [part.id]: 1 }));
  };

  // Selected fabric product from API data
  const selectedFabric = useMemo(() => {
    return fabricProducts.find(p => p.fabricType === fabricType) || null;
  }, [fabricType, fabricProducts]);

  // Available colors for selected fabric type (from inventory)
  const fabricColorOptions = useMemo(() => {
    return selectedFabric?.colors || [];
  }, [selectedFabric]);

  // Calculate fabric price per line — matches factory BOM calculation exactly
  // Uses real cut formulas, prorated costs from stock length, and seam detection
  const calcFabricLinePrice = (w: number, h: number, windowPanel = false, windowCount = 0) => {
    if (!selectedFabric || selectedFabric.unitCost <= 0 || selectedFabric.stockLength <= 0) return null;
    if (!w || w <= 0 || !h || h <= 0) return null;

    // Fabric: cut formula = W + 6, prorated from stock roll
    const fabricCutLength = w + 6;
    const fabricProrated = (fabricCutLength / selectedFabric.stockLength) * selectedFabric.unitCost;
    // Seam detection: if height + 10 > maxPanelCut, need 2 pieces
    const fabricHeight = h + 10; // BOM uses H_system + 10 for seam check
    const needsSeam = selectedFabric.maxPanelCut > 0 && fabricHeight >= selectedFabric.maxPanelCut;
    const fabricQty = needsSeam ? 2 : 1;
    const fabricBomCost = fabricProrated * fabricQty;

    let totalBomCost = fabricBomCost;

    if (fabricOrderType === 'panel_replacement' && assemblyParts) {
      // 6mm Keder Spline: cut = (W * 2) + 6, prorated from stock
      const keder6mmCut = (w * 2) + 6;
      const keder6mmCost = (keder6mmCut / assemblyParts.kederSpline6mm.stockLength) * assemblyParts.kederSpline6mm.unitCost;
      // 4mm Keder Spline: cut = (H * 2) + 10, prorated from stock
      const keder4mmCut = (h * 2) + 10;
      const keder4mmCost = (keder4mmCut / assemblyParts.kederSpline4mm.stockLength) * assemblyParts.kederSpline4mm.unitCost;
      const patchCost = assemblyParts.cornerPatches.costPerScreen;
      totalBomCost += keder6mmCost + keder4mmCost + patchCost;

      // Strataglass windows: unit cost per window (matches BOM page)
      if (windowPanel && windowCount > 0 && assemblyParts.strataglass) {
        totalBomCost += assemblyParts.strataglass.unitCost * windowCount;
      }
    }

    const msrp = totalBomCost * 5;
    const dealer = msrp * (1 - dealerDiscount / 100);
    return { msrp, dealer, needsSeam, windowPanel, windowCount };
  };

  // Total price preview across all lines
  const fabricTotalPreview = useMemo(() => {
    if (!selectedFabric || selectedFabric.unitCost <= 0 || selectedFabric.stockLength <= 0) return null;
    let totalMsrp = 0;
    let totalDealer = 0;
    let validLines = 0;

    for (const line of fabricLines) {
      const w = parseFloat(line.width);
      const h = parseFloat(line.height);
      const price = calcFabricLinePrice(w, h, line.windowPanel, line.windowCount);
      if (price) {
        totalMsrp += price.msrp * line.qty;
        totalDealer += price.dealer * line.qty;
        validLines++;
      }
    }

    return validLines > 0 ? { totalMsrp, totalDealer, lineCount: validLines } : null;
  }, [selectedFabric, fabricLines, dealerDiscount, fabricOrderType, assemblyParts]);

  // Add all fabric lines to cart
  const addFabricToCart = () => {
    if (!selectedFabric) { setError('Please select a fabric type'); return; }
    if (!fabricColor) { setError('Please select a fabric color'); return; }
    if (selectedFabric.unitCost <= 0 || selectedFabric.stockLength <= 0) { setError('Pricing not available for this fabric — contact us'); return; }

    const newItems: CartItem[] = [];
    const typeLabel = fabricOrderType === 'panel_replacement' ? 'Panel Replacement' : 'Fabric Only';

    for (const line of fabricLines) {
      const w = parseFloat(line.width);
      const h = parseFloat(line.height);
      if (!w || w <= 0 || !h || h <= 0) continue;

      const price = calcFabricLinePrice(w, h, line.windowPanel, line.windowCount);
      if (!price) continue;

      const windowLabel = line.windowPanel && line.windowCount > 0
        ? ` + ${line.windowCount} window${line.windowCount > 1 ? 's' : ''}`
        : '';

      newItems.push({
        id: `fabric-${selectedFabric.fabricType}-${fabricColor}-${line.id}-${Date.now()}`,
        category: 'fabric',
        name: `${selectedFabric.fabricType} ${typeLabel} - ${fabricColor}${windowLabel}`,
        description: `${w}" W × ${h}" H${price.needsSeam ? ' (seam)' : ''}${windowLabel}`,
        quantity: line.qty,
        unitPrice: price.dealer,
        msrpPrice: price.msrp,
        fabricType: selectedFabric.fabricType,
        fabricColor: fabricColor,
        fabricOrderType,
        width: w,
        height: h,
      });
    }

    if (newItems.length === 0) { setError('Please enter valid width and height for at least one line'); return; }

    setCart(prev => [...prev, ...newItems]);
    setFabricLines([{ id: '1', width: '', height: '', qty: 1, windowPanel: false, windowCount: 1 }]);
    setError('');
  };

  // Remove from cart
  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  // Update cart quantity
  const updateCartQty = (id: string, qty: number) => {
    if (qty <= 0) {
      removeFromCart(id);
    } else {
      setCart(prev => prev.map(item =>
        item.id === id ? { ...item, quantity: qty } : item
      ));
    }
  };

  // Calculate totals
  const cartSubtotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  }, [cart]);

  // Compute shipping cost first so it can roll into the tax preview base
  const shippingCost = useMemo(() => {
    if (shippingMethod === 'willcall') return 0;
    if (carrierType === 'small_package') return selectedParcelQuote?.totalAmount || 0;
    if (carrierType === 'mixed') return (selectedParcelQuote?.totalAmount || 0) + (freightInfo?.rate || 0);
    return freightInfo?.rate || 0;
  }, [shippingMethod, carrierType, selectedParcelQuote, freightInfo]);

  // Sales tax preview from server (authoritative source = TaxService).
  // Taxable base includes shipping so changing carrier re-fires tax.
  const [taxPreview, setTaxPreview] = useState(0);
  const [taxRatePct, setTaxRatePct] = useState(0);
  useEffect(() => {
    const taxableBase = cartSubtotal + shippingCost;
    if (taxableBase <= 0) { setTaxPreview(0); setTaxRatePct(0); return; }
    const params = new URLSearchParams({
      taxableAmount: String(taxableBase),
      willCall: shippingMethod === 'willcall' ? 'true' : 'false',
    });
    if (shippingMethod !== 'willcall' && shipState) params.append('shipToState', shipState);
    const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/tax/preview?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { tax: 0, ratePercent: 0 })
      .then(d => { setTaxPreview(parseFloat(d.tax) || 0); setTaxRatePct(parseFloat(d.ratePercent) || 0); })
      .catch(() => { setTaxPreview(0); setTaxRatePct(0); });
  }, [cartSubtotal, shippingMethod, shipState, shippingCost]);

  const cartTotal = useMemo(() => {
    return cartSubtotal + shippingCost + taxPreview;
  }, [cartSubtotal, shippingCost, taxPreview]);

  // PO Box detection (client-side)
  const checkPOBox = (address: string): boolean => {
    if (!address) return false;
    const normalized = address.trim().toLowerCase().replace(/[.,#]/g, '');
    return /\bp\s*o\s*box\b/.test(normalized) || /\bpost\s+office\s+box\b/.test(normalized) || /^box\s+\d/.test(normalized);
  };

  // Get LTL freight quote (existing FreightView flow)
  const getLTLFreightQuote = async () => {
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    const maxLength = cart
      .filter(item => item.category === 'uchannel' && item.lengthInches)
      .reduce((max, item) => Math.max(max, item.lengthInches || 0), 48);

    const length = Math.max(48, Math.ceil(maxLength));
    const width = 24;
    const height = Math.max(12, Math.ceil(totalItems / 5) * 6);
    const weight = Math.max(25, totalItems * 3);

    const result = await freightApi.estimate({
      destination_zip: shipZip,
      length, width, height, weight,
      pallets: 1,
      freight_class: 85,
      accessorials: { liftgate, residential, inside: false },
    });

    if (result.success) {
      const quotes = result.allQuotes || [];
      setFreightQuotes(quotes.slice(0, 5));
      const cheapest = quotes[0];
      setFreightInfo({
        carrier: cheapest?.carrierName || result.carrier,
        service: cheapest?.serviceDescription || result.service,
        transitTime: cheapest?.transitTime || result.transitTime,
        rate: cheapest?.totalAmount || result.rate,
        fallback: result.fallback,
        freightviewShipmentId: result.freightviewShipmentId,
        selectedQuoteId: cheapest?.quoteId,
        selectedCarrierCode: cheapest?.carrierCode,
      });
    } else {
      setFreightError(result.error || 'Unable to get freight quote.');
    }
  };

  // Get shipping quote — smart routing: small package vs LTL freight
  const getShippingQuote = async () => {
    if (shippingMethod === 'willcall') {
      setFreightInfo(null);
      setParcelQuotes([]);
      setSelectedParcelQuote(null);
      setCarrierType(null);
      return;
    }

    if (!shipZip || shipZip.length !== 5) {
      setFreightError('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (cart.length === 0) {
      setFreightError('Please add items to cart first');
      return;
    }

    setFreightLoading(true);
    setFreightError('');
    setFreightInfo(null);
    setFreightQuotes([]);
    setParcelQuotes([]);
    setSelectedParcelQuote(null);
    setPoBoxWarning(false);

    try {
      // Step 1: Classify cart
      const cartItems = cart.map(item => ({
        category: item.category,
        quantity: item.quantity,
        sku: item.sku,
        lengthInches: item.lengthInches,
        weight: undefined,
      }));

      const classification = await parcelApi.classifyCart(cartItems);
      setCarrierType(classification.type);

      // Normalize ZIP (strip ZIP+4) and state for carrier APIs
      const cleanZip = shipZip.replace(/-.*$/, '').slice(0, 5);

      // Step 2: Get quotes based on classification
      if (classification.type === 'small_package') {
        // Only small package quotes
        const rateResult = await parcelApi.getRates({
          // Sent so the API can withhold carriers it cannot produce a label
          // for on items that must ship end-to-end.
          skus: cart.map(i => i.sku).filter(Boolean) as string[],
          destinationZip: cleanZip,
          destinationCity: shipCity || undefined,
          destinationState: shipState || undefined,
          destinationAddress: shipStreet || undefined,
          residential,
          packages: classification.suggestedPackages,
        });
        if (rateResult.success && rateResult.quotes && rateResult.quotes.length > 0) {
          setParcelQuotes(rateResult.quotes);
          setPoBoxWarning(rateResult.poBoxWarning || false);
        } else {
          setFreightError('Unable to get shipping quotes. Please check your address and try again.');
        }
      } else if (classification.type === 'ltl_freight') {
        // Only LTL freight
        await getLTLFreightQuote();
      } else if (classification.type === 'mixed') {
        // Both in parallel
        const [parcelResult] = await Promise.allSettled([
          parcelApi.getRates({
            destinationZip: cleanZip,
            destinationCity: shipCity || undefined,
            destinationState: shipState || undefined,
            destinationAddress: shipStreet || undefined,
            residential,
            packages: classification.suggestedPackages,
            skus: cart.map(i => i.sku).filter(Boolean) as string[],
          }),
          getLTLFreightQuote(),
        ]);
        if (parcelResult.status === 'fulfilled' && parcelResult.value.success && parcelResult.value.quotes) {
          setParcelQuotes(parcelResult.value.quotes);
          setPoBoxWarning(parcelResult.value.poBoxWarning || false);
        }
      }
    } catch (err: any) {
      console.error('Shipping quote error:', err);
      setFreightError('Error getting shipping quote. Please try again or call for a quote.');
    } finally {
      setFreightLoading(false);
    }
  };

  // Filter parts by search
  const filteredParts = useMemo(() => {
    if (!partsSearch) return apiParts;
    const search = partsSearch.toLowerCase();
    return apiParts.filter(part =>
      part.name.toLowerCase().includes(search) ||
      part.sku.toLowerCase().includes(search) ||
      part.category.toLowerCase().includes(search)
    );
  }, [partsSearch, apiParts]);

  // Handle checkout - Creates Stripe payment
  const handleCheckout = async (paymentMethodType: 'card' | 'us_bank_account' = 'card') => {
    const isBank = paymentMethodType === 'us_bank_account';
    if (cart.length === 0) {
      setError('Your cart is empty');
      return;
    }

    if (shippingMethod === 'ship') {
      if (!shipStreet || !shipCity || !shipState || !shipZip) {
        setError('Please complete shipping address');
        return;
      }
      if (carrierType === 'small_package' && !selectedParcelQuote) {
        setError('Please select a shipping option');
        return;
      }
      if (carrierType === 'ltl_freight' && !freightInfo) {
        setError('Please click "Get Shipping Quote" to get shipping cost');
        return;
      }
      if (carrierType === 'mixed' && (!selectedParcelQuote || !freightInfo)) {
        setError('Please get quotes for all items');
        return;
      }
      if (!carrierType && !freightInfo && !selectedParcelQuote) {
        setError('Please click "Get Shipping Quote" to get shipping cost');
        return;
      }
    }

    setProcessing(true);
    setError('');

    try {
      const u = user as any;

      // Build order data with all item details
      const orderItems = cart.map(item => ({
        type: item.category,
        name: item.name,
        description: item.description,
        sku: item.sku,
        color: item.color,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        msrpPrice: item.msrpPrice,
        unit: item.unit,
        lengthInches: item.lengthInches,
        exactLength: item.exactLength,
        totalPrice: item.unitPrice * item.quantity,
        isBackorder: item.isBackorder || false,
        fabricType: item.fabricType,
        fabricColor: item.fabricColor,
        fabricOrderType: item.fabricOrderType,
        width: item.width,
        height: item.height,
      }));

      // Calculate shipping cost
      const shippingCost = shippingMethod === 'willcall' ? 0
        : carrierType === 'small_package' ? (selectedParcelQuote?.totalAmount || 0)
        : carrierType === 'mixed' ? (selectedParcelQuote?.totalAmount || 0) + (freightInfo?.rate || 0)
        : (freightInfo?.rate || 0);

      const orderData = {
        orderType: 'parts',
        items: orderItems,
        subtotal: cartSubtotal,
        shipping: shippingCost,
        total: cartTotal,
        sidemark: sidemark.trim() || undefined,
        poNumber: poNumber.trim() || undefined,
        shippingMethod,
        shippingAddress: shippingMethod === 'willcall' ? null : {
          street: shipStreet,
          city: shipCity,
          state: shipState,
          zip: shipZip,
        },
        freightInfo: shippingMethod === 'willcall' ? { willCall: true } : freightInfo,
        // Structured freight fields for orders table columns
        freightviewShipmentId: freightInfo?.freightviewShipmentId || null,
        selectedQuoteId: freightInfo?.selectedQuoteId || null,
        selectedCarrierName: freightInfo?.carrier || null,
        selectedCarrierCode: freightInfo?.selectedCarrierCode || null,
        freightTotalAmount: freightInfo?.rate || null,
        // Parcel (small package) shipping data
        shippingCarrierType: carrierType || (freightInfo ? 'ltl_freight' : null),
        parcelCarrier: selectedParcelQuote?.carrier || null,
        parcelServiceCode: selectedParcelQuote?.serviceCode || null,
        parcelServiceName: selectedParcelQuote?.serviceName || null,
        parcelTotalAmount: selectedParcelQuote?.totalAmount || null,
        parcelQuotes: parcelQuotes.length > 0 ? parcelQuotes : null,
        dealer: {
          id: u?.contactId || u?.id,
          dealerId: u?.dealerId,
          email: user?.email,
          company: u?.companyName || u?.company,
          contactName: user?.name,
        },
      };

      console.log(`[PartsPage] Creating ${isBank ? 'ACH' : 'card'} checkout`);

      // Edit mode: PATCH the existing order in place before opening checkout so
      // sidemark/PO/cart edits land on the same order. Stripe session reuses it
      // via existingOrderId.
      if (editingOrderId) {
        try {
          await ordersApi.updateParts(editingOrderId, orderData);
          console.log(`[PartsPage] Updated existing parts order ${editingOrderNumber || editingOrderId}`);
        } catch (err: any) {
          console.error('[PartsPage] Failed to update existing parts order:', err);
          setError(err?.response?.data?.message || err.message || 'Failed to save changes to order');
          setProcessing(false);
          return;
        }
      }

      // Create Stripe checkout session
      const result = await paymentApi.createCheckout({
        invoice: {
          items: orderItems,
          subtotal: cartSubtotal,
          shipping: shippingCost,
          total: cartTotal,
        },
        order: orderData,
        dealer: orderData.dealer,
        paymentAmount: Math.round(cartTotal * 100), // Convert to cents
        orderType: 'parts',
        paymentMethodType,
        existingOrderId: editingOrderId || undefined,
      } as any);

      if (result.success && result.checkoutUrl) {
        // Save shipping preference for next time
        const shippingPrefs = {
          shippingMethod,
          address: shippingMethod === 'ship' ? {
            street: shipStreet,
            city: shipCity,
            state: shipState,
            zip: shipZip,
            liftgate,
            residential,
          } : null,
        };
        localStorage.setItem('partsShippingPrefs', JSON.stringify(shippingPrefs));

        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        setError(result.error || 'Failed to create payment session');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Checkout failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // ─── Authorize.net helpers ────────────────────────────────────────

  /** Build order data shared between Stripe and Authorize.net flows */
  const buildPartsOrderData = () => {
    const u = user as any;
    const orderItems = cart.map(item => ({
      type: item.category,
      name: item.name,
      description: item.description,
      sku: item.sku,
      color: item.color,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      msrpPrice: item.msrpPrice,
      unit: item.unit,
      lengthInches: item.lengthInches,
      exactLength: item.exactLength,
      totalPrice: item.unitPrice * item.quantity,
      isBackorder: item.isBackorder || false,
      fabricType: item.fabricType,
      fabricColor: item.fabricColor,
      fabricOrderType: item.fabricOrderType,
      width: item.width,
      height: item.height,
    }));

    const shippingCost = shippingMethod === 'willcall' ? 0
      : carrierType === 'small_package' ? (selectedParcelQuote?.totalAmount || 0)
      : carrierType === 'mixed' ? (selectedParcelQuote?.totalAmount || 0) + (freightInfo?.rate || 0)
      : (freightInfo?.rate || 0);

    return {
      items: orderItems,
      subtotal: cartSubtotal,
      shipping: shippingCost,
      total: cartTotal,
      sidemark: sidemark.trim() || undefined,
      poNumber: poNumber.trim() || undefined,
      shippingMethod,
      shippingAddress: shippingMethod === 'willcall' ? null : {
        street: shipStreet,
        city: shipCity,
        state: shipState,
        zip: shipZip,
      },
      freightInfo: shippingMethod === 'willcall' ? { willCall: true } : freightInfo,
      freightviewShipmentId: freightInfo?.freightviewShipmentId || null,
      selectedQuoteId: freightInfo?.selectedQuoteId || null,
      selectedCarrierName: freightInfo?.carrier || null,
      selectedCarrierCode: freightInfo?.selectedCarrierCode || null,
      freightTotalAmount: freightInfo?.rate || null,
      shippingCarrierType: carrierType || (freightInfo ? 'ltl_freight' : null),
      parcelCarrier: selectedParcelQuote?.carrier || null,
      parcelServiceCode: selectedParcelQuote?.serviceCode || null,
      parcelServiceName: selectedParcelQuote?.serviceName || null,
      parcelTotalAmount: selectedParcelQuote?.totalAmount || null,
      parcelQuotes: parcelQuotes.length > 0 ? parcelQuotes : null,
      dealer: {
        id: u?.contactId || u?.id,
        dealerId: u?.dealerId,
        email: user?.email,
        company: u?.companyName || u?.company,
        contactName: user?.name,
      },
    };
  };

  /** Wait for Accept.js to be ready (loaded from index.html <head>) */
  const loadAcceptJs = async () => {
    if ((window as any).Accept) return;
    // Accept.js is loaded in index.html — poll until ready
    await new Promise<void>((resolve, reject) => {
      let attempts = 0;
      const checkReady = () => {
        if ((window as any).Accept) { resolve(); return; }
        attempts++;
        if (attempts > 100) { reject(new Error('Accept.js failed to load')); return; }
        setTimeout(checkReady, 100);
      };
      checkReady();
    });
  };

  /** Tokenize card data via Accept.js */
  const tokenizeWithAcceptJs = (secureData: any): Promise<{ dataDescriptor: string; dataValue: string }> => {
    return new Promise((resolve, reject) => {
      (window as any).Accept.dispatchData(secureData, (response: any) => {
        if (response.messages.resultCode === 'Error') {
          const code = response.messages.message[0]?.code || '';
          const text = response.messages.message[0]?.text || 'Card tokenization failed';
          console.error('[Accept.js] Error code:', code, 'Text:', text, 'Full response:', JSON.stringify(response.messages));
          reject(new Error(`${text} (${code})`));
        } else {
          resolve({ dataDescriptor: response.opaqueData.dataDescriptor, dataValue: response.opaqueData.dataValue });
        }
      });
    });
  };

  /** Open Authorize.net card modal */
  const openAuthorizeModal = (tab: 'card' | 'saved' | 'bank' = 'card') => {
    setCardForm({ cardNumber: '', expMonth: '', expYear: '', cvv: '', saveCard: false });
    setCardProcessing(false);
    setSelectedSavedMethod(null);
    setCardModalTab(tab);
    setShowCardModal(true);
    // Load saved methods from both Authorize.net (cards) and Helcim (bank accounts)
    setLoadingSavedMethods(true);
    Promise.all([
      authorizeApi.getSavedMethods().catch(() => []),
      helcimApi.getSavedBankAccounts().catch(() => []),
    ])
      .then(([authMethods, helcimBanks]) => {
        const cards = authMethods.filter(m => m.paymentType === 'card');
        const allMethods = [...cards, ...helcimBanks.map(b => ({ ...b, provider: 'helcim' as const }))];
        setSavedMethods(allMethods);
        if (allMethods.length > 0) setSelectedSavedMethod(allMethods[0].id);
      })
      .catch(() => setSavedMethods([]))
      .finally(() => setLoadingSavedMethods(false));
  };

  /** Pay with new card via Authorize.net */
  const handleAuthorizeNetPayment = async () => {
    setCardProcessing(true);
    setError('');

    try {
      await loadAcceptJs();

      const apiLoginId = import.meta.env.VITE_AUTHORIZE_API_LOGIN_ID;
      const clientKey = import.meta.env.VITE_AUTHORIZE_CLIENT_KEY;
      if (!apiLoginId || !clientKey) {
        setError('Direct card payment is not configured.');
        setCardProcessing(false);
        return;
      }

      // Tokenize card
      const opaqueData = await tokenizeWithAcceptJs({
        authData: { apiLoginID: apiLoginId, clientKey },
        cardData: {
          cardNumber: cardForm.cardNumber.replace(/\s/g, ''),
          month: cardForm.expMonth.padStart(2, '0'),
          year: cardForm.expYear.length === 2 ? `20${cardForm.expYear}` : cardForm.expYear,
          cardCode: cardForm.cvv,
        },
      });

      // Create the pending parts order first
      const orderData = buildPartsOrderData();
      const pendingOrder = await paymentApi.createPendingPartsOrder(orderData);

      // Charge via Authorize.net
      const result = await authorizeApi.charge({
        opaqueDataDescriptor: opaqueData.dataDescriptor,
        opaqueDataValue: opaqueData.dataValue,
        amount: Math.round(cartTotal * 100),
        orderId: pendingOrder.orderId,
        orderNumber: pendingOrder.orderNumber,
        dealer: {
          email: user?.email || '',
          company: (user as any)?.companyName || (user as any)?.company,
          contactName: user?.name,
        },
        saveForFuture: cardForm.saveCard,
      });

      if (result.success) {
        // Save shipping prefs
        const shippingPrefs = {
          shippingMethod,
          address: shippingMethod === 'ship' ? { street: shipStreet, city: shipCity, state: shipState, zip: shipZip, liftgate, residential } : null,
        };
        localStorage.setItem('partsShippingPrefs', JSON.stringify(shippingPrefs));

        setShowCardModal(false);
        setSuccess(`Payment successful! Order ${pendingOrder.orderNumber} has been placed.`);
        setCart([]);
      } else {
        setError(result.error || 'Payment failed. Please try again.');
      }
    } catch (err: any) {
      console.error('[PartsPage] Authorize.net error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setCardProcessing(false);
    }
  };

  /** Pay with saved card/bank */
  const handleSavedMethodPayment = async () => {
    if (!selectedSavedMethod) return;
    setCardProcessing(true);
    setError('');

    try {
      // Create the pending parts order first
      const orderData = buildPartsOrderData();
      const pendingOrder = await paymentApi.createPendingPartsOrder(orderData);

      // Check if selected method is a Helcim bank account
      const selectedMethod = savedMethods.find(m => m.id === selectedSavedMethod);
      const isHelcim = (selectedMethod as any)?.provider === 'helcim';

      let result: { success: boolean; transactionId?: string; error?: string };

      if (isHelcim) {
        result = await helcimApi.chargeSavedBank({
          profileId: selectedSavedMethod,
          amount: Math.round(cartTotal * 100),
          orderId: pendingOrder.orderId,
          orderNumber: pendingOrder.orderNumber,
        });
      } else {
        result = await authorizeApi.chargeSaved({
          paymentProfileId: selectedSavedMethod,
          amount: Math.round(cartTotal * 100),
          orderId: pendingOrder.orderId,
          orderNumber: pendingOrder.orderNumber,
        });
      }

      if (result.success) {
        const shippingPrefs = {
          shippingMethod,
          address: shippingMethod === 'ship' ? { street: shipStreet, city: shipCity, state: shipState, zip: shipZip, liftgate, residential } : null,
        };
        localStorage.setItem('partsShippingPrefs', JSON.stringify(shippingPrefs));

        setShowCardModal(false);
        setSuccess(`Payment successful! Order ${pendingOrder.orderNumber} has been placed.`);
        setCart([]);
      } else {
        setError(result.error || 'Payment failed. Please try again.');
      }
    } catch (err: any) {
      console.error('[PartsPage] Saved method error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setCardProcessing(false);
    }
  };

  /** Pay with bank account (ACH) via Helcim */
  const handleBankAccountPayment = async () => {
    // If no address on file, validate inline form first
    if (!hasAddress) {
      if (!achAddressForm.city.trim() || !achAddressForm.state.trim() || !achAddressForm.zip.trim()) {
        setError('Please enter your city, state, and ZIP code to proceed with ACH payment.');
        return;
      }
      if (!/^\d{5}(-\d{4})?$/.test(achAddressForm.zip.trim())) {
        setError('Please enter a valid ZIP code.');
        return;
      }
    }

    setCardProcessing(true);
    setError('');

    try {
      // Save address if entered from inline form
      if (!hasAddress && achAddressForm.zip.trim()) {
        await authApi.updateAddress({
          street: achAddressForm.street.trim(),
          city: achAddressForm.city.trim(),
          state: achAddressForm.state.trim(),
          zip: achAddressForm.zip.trim(),
        });
      }

      // Create the pending parts order first
      const orderData = buildPartsOrderData();
      const pendingOrder = await paymentApi.createPendingPartsOrder(orderData);

      // 1. Initialize HelcimPay.js checkout session
      const session = await helcimApi.initializeCheckout({
        amount: Math.round(cartTotal * 100),
        orderId: pendingOrder.orderId,
        orderNumber: pendingOrder.orderNumber,
        dealer: {
          email: user?.email || '',
          company: (user as any)?.companyName || (user as any)?.company || '',
          contactName: user?.name || '',
        },
      });

      setHelcimCheckoutToken(session.checkoutToken);
      setHelcimSecretToken(session.secretToken);

      // 2. Load HelcimPay.js script if not already loaded
      if (!(window as any).appendHelcimPayIframe) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://secure.helcim.app/helcim-pay/services/start.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Helcim payment form'));
          document.head.appendChild(script);
        });
      }

      // 3. Listen for HelcimPay.js events
      const eventHandler = async (event: MessageEvent) => {
        const eventData = event.data;
        if (!eventData || typeof eventData !== 'object') return;

        const eventName = eventData.eventName || '';
        if (!eventName.includes('helcim-pay-js')) return;

        const eventStatus = eventData.eventStatus;

        if (eventStatus === 'SUCCESS') {
          window.removeEventListener('message', eventHandler);
          try {
            let txData = eventData.eventMessage;
            if (typeof txData === 'string') {
              try { txData = JSON.parse(txData); } catch { /* keep as string */ }
            }

            const result = await helcimApi.validatePayment({
              checkoutToken: session.checkoutToken,
              secretToken: session.secretToken,
              transactionData: txData,
              orderId: pendingOrder.orderId,
              amountCents: session.chargedAmountCents,
              achDiscountPercent: session.achDiscountPercent,
              achDiscountAmountCents: session.achDiscountAmountCents,
            });

            if (result.success) {
              const shippingPrefs = {
                shippingMethod,
                address: shippingMethod === 'ship' ? { street: shipStreet, city: shipCity, state: shipState, zip: shipZip, liftgate, residential } : null,
              };
              localStorage.setItem('partsShippingPrefs', JSON.stringify(shippingPrefs));

              setShowCardModal(false);
              setHelcimCheckoutToken(null);
              setHelcimSecretToken(null);
              const discountNote = session.achDiscountPercent > 0
                ? ` (${session.achDiscountPercent}% ACH discount applied!)`
                : '';
              setSuccess(`ACH payment successful! Order ${pendingOrder.orderNumber} has been placed.${discountNote}`);
              setCart([]);
            } else {
              setError(result.error || 'Payment validation failed.');
            }
          } catch (valErr: any) {
            console.error('[PartsPage] ACH validation error:', valErr);
            setError(valErr.message || 'Payment validation failed.');
          } finally {
            setCardProcessing(false);
          }
        } else if (eventStatus === 'ABORTED') {
          window.removeEventListener('message', eventHandler);
          setCardProcessing(false);
          setHelcimCheckoutToken(null);
          setHelcimSecretToken(null);
        }
      };

      window.addEventListener('message', eventHandler);

      // 4. Open the HelcimPay.js modal
      (window as any).appendHelcimPayIframe(session.checkoutToken);

    } catch (err: any) {
      console.error('[PartsPage] ACH payment error:', err);
      setError(err.response?.data?.message || err.message || 'ACH payment initialization failed. Please try again.');
      setCardProcessing(false);
    }
  };

  /** Submit order for invoice — no payment required */
  const handleSubmitForInvoice = async () => {
    if (cart.length === 0) {
      setError('Your cart is empty');
      return;
    }

    setShowInvoiceConfirm(false);

    try {
      setProcessing(true);
      setError('');

      const orderData = buildPartsOrderData();

      // Edit mode: PATCH the existing order in place — don't create a new one.
      if (editingOrderId) {
        const updated = await ordersApi.updateParts(editingOrderId, orderData);
        setSuccess(`Order ${updated.orderNumber} updated.`);
        setEditingOrderId(null);
        setEditingOrderNumber(null);
        return;
      }

      const result = await paymentApi.submitPartsForInvoice(orderData);

      if (result.orderId) {
        setCart([]);
        setSuccess(`Order ${result.orderNumber} submitted for invoice ($${cartTotal.toFixed(2)}). Record payment in factory admin.`);
      }
    } catch (err: any) {
      console.error('Submit for invoice error:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to submit order for invoice');
    } finally {
      setProcessing(false);
    }
  };

  /** Delete a saved payment method */
  const handleDeleteSavedMethod = async (id: string) => {
    if (!confirm('Remove this saved payment method?')) return;
    try {
      const method = savedMethods.find(m => m.id === id);
      const isHelcim = (method as any)?.provider === 'helcim';
      if (isHelcim) {
        await helcimApi.deleteSavedBankAccount(id);
      } else {
        await authorizeApi.deleteSavedMethod(id);
      }
      setSavedMethods(prev => prev.filter(m => m.id !== id));
      if (selectedSavedMethod === id) setSelectedSavedMethod(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete payment method');
    }
  };

  return (
    <>
      <PageHeader
        title="Parts & Accessories"
        subtitle={fromOrder ? "Add accessories to your screen order" : "Order U-channel, remotes, replacement parts, and more"}
      />

      {/* From Order Banner */}
      {fromOrder && (
        <div className="mb-6 p-4 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-500 rounded-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-white font-medium">Adding accessories to your order</p>
                <p className="text-purple-300 text-sm">
                  {cart.length > 0
                    ? `${cart.length} item${cart.length !== 1 ? 's' : ''} in cart - will be added to your order`
                    : 'Browse and add items, or return without adding anything'}
                </p>
              </div>
            </div>
            <button
              onClick={returnToOrder}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                cart.length > 0
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-500 text-gray-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              {cart.length > 0 ? `Return with ${cart.length} Item${cart.length !== 1 ? 's' : ''}` : 'Return Without Adding'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* U-CHANNEL SECTION - UPDATED */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                U-Channel
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Input mode:</span>
                <button
                  type="button"
                  onClick={() => setFractionMode(!fractionMode)}
                  className={clsx(
                    'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
                    fractionMode
                      ? 'bg-keder-yellow text-black'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  )}
                >
                  {fractionMode ? '⅝ Fractions' : '.125 Decimal'}
                </button>
              </div>
            </div>

            <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-500 rounded-lg text-sm text-blue-800 dark:text-blue-200">
              <strong>Pricing:</strong> ${UCHANNEL_PRICING.dealer.toFixed(2)} per foot
            </div>

            {/* Multiple U-Channel Line Items */}
            <div className="space-y-4">
              {uChannelItems.map((item, index) => (
                <div key={item.id} className="bg-keder-darker rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-white font-medium">Line {index + 1}</span>
                    {uChannelItems.length > 1 && (
                      <button
                        onClick={() => removeUChannelLine(item.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="label text-xs">Color</label>
                      <select
                        value={item.color}
                        onChange={e => updateUChannelLine(item.id, 'color', e.target.value)}
                        className="input-field text-sm"
                      >
                        {UCHANNEL_COLORS.map(color => (
                          <option key={color.value} value={color.value}>{color.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label text-xs">Length (INCHES)</label>
                      <DimensionInput
                        value={item.lengthInches}
                        fractionMode={fractionMode}
                        onChange={(val) => updateUChannelLine(item.id, 'lengthInches', val)}
                        placeholder={fractionMode ? 'e.g. 96 5/16' : 'e.g. 96'}
                        min="1"
                        className="input-field text-sm"
                      />
                    </div>

                    <div>
                      <label className="label text-xs">Quantity</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={e => updateUChannelLine(item.id, 'quantity', parseInt(e.target.value) || 1)}
                        onWheel={(e) => e.currentTarget.blur()}
                        min="1"
                        className="input-field text-sm"
                      />
                    </div>

                    <div>
                      <label className="label text-xs">Length Option</label>
                      <div className="flex flex-col gap-1 mt-1">
                        <label className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                          <input
                            type="radio"
                            name={`length-option-${item.id}`}
                            checked={!item.exactLength}
                            onChange={() => updateUChannelLine(item.id, 'exactLength', false)}
                            className="text-keder-yellow"
                          />
                          Factory Default (+6")
                        </label>
                        <label className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                          <input
                            type="radio"
                            name={`length-option-${item.id}`}
                            checked={item.exactLength}
                            onChange={() => updateUChannelLine(item.id, 'exactLength', true)}
                            className="text-keder-yellow"
                          />
                          Exact Length
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Show calculated price for this line */}
                  {parseFloat(item.lengthInches) > 0 && (
                    <div className="mt-3 text-sm text-gray-400">
                      {(() => {
                        const { dealerPrice, actualLengthInches, feet } = calculateUChannelPrice(
                          parseFloat(item.lengthInches),
                          item.exactLength
                        );
                        return (
                          <span>
                            Final length: <span className="text-white">{actualLengthInches}"</span> ({feet.toFixed(2)} ft) =
                            <span className="text-keder-yellow font-medium ml-1">
                              ${(dealerPrice * item.quantity).toFixed(2)}
                            </span>
                            {!item.exactLength && <span className="text-blue-400 ml-2">(includes +6" factory default)</span>}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Another / Add to Cart buttons */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={addUChannelLine}
                className="btn-secondary flex-1"
              >
                + Add Another Line
              </button>
              <button
                onClick={addUChannelToCart}
                disabled={!uChannelItems.some(item => parseFloat(item.lengthInches) > 0)}
                className="btn-primary flex-1"
              >
                Add to Cart
              </button>
            </div>
          </div>

          {/* FABRIC SECTION */}
          <div className="card">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              Fabric Panels
              <span className="ml-2 px-2 py-0.5 text-xs bg-teal-600 text-white rounded-full">Doors, Fixed Screens & Replacements</span>
            </h2>

            <div className="mb-4 p-3 bg-teal-900/30 border border-teal-500 rounded-lg text-sm text-teal-200">
              <strong>Fabric & Panel Replacements:</strong> Order fabric only (raw material) or a complete panel replacement (fabric + keder spline + corner patches).
              {rewardTierDisplay ? (
                <span> Your {rewardTierDisplay} pricing is applied automatically.</span>
              ) : dealerDiscount > 0 ? (
                <span> Your dealer pricing is applied automatically.</span>
              ) : null}
            </div>

            {/* Order Type Toggle */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={() => setFabricOrderType('panel_replacement')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  fabricOrderType === 'panel_replacement'
                    ? 'bg-teal-600 text-white'
                    : 'bg-keder-darker text-gray-400 hover:text-white'
                }`}
              >
                Panel Replacement
                <span className="block text-xs opacity-75 mt-0.5">Fabric + Keder Spline + Patches</span>
              </button>
              <button
                onClick={() => setFabricOrderType('fabric_only')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  fabricOrderType === 'fabric_only'
                    ? 'bg-teal-600 text-white'
                    : 'bg-keder-darker text-gray-400 hover:text-white'
                }`}
              >
                Fabric Only
                <span className="block text-xs opacity-75 mt-0.5">Raw fabric material only</span>
              </button>
            </div>

            {fabricProducts.length === 0 ? (
              <div className="bg-keder-darker rounded-lg p-6 text-center text-gray-400">
                Loading fabric data from inventory...
              </div>
            ) : (
            <div className="bg-keder-darker rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label text-xs">Fabric Type</label>
                  <select
                    value={fabricType}
                    onChange={e => { setFabricType(e.target.value); setFabricColor(''); }}
                    className="input-field w-full text-sm"
                  >
                    <option value="">Select Fabric Type...</option>
                    {fabricProducts.filter(p => p.category === 'solar').length > 0 && (
                      <optgroup label="Solar Mesh">
                        {fabricProducts.filter(p => p.category === 'solar').map(p => (
                          <option key={p.fabricType} value={p.fabricType}>{p.fabricType}</option>
                        ))}
                      </optgroup>
                    )}
                    {fabricProducts.filter(p => p.category === 'premium').length > 0 && (
                      <optgroup label="Premium Solar">
                        {fabricProducts.filter(p => p.category === 'premium').map(p => (
                          <option key={p.fabricType} value={p.fabricType}>{p.fabricType}</option>
                        ))}
                      </optgroup>
                    )}
                    {fabricProducts.filter(p => p.category === 'vinyl').length > 0 && (
                      <optgroup label="Vinyl">
                        {fabricProducts.filter(p => p.category === 'vinyl').map(p => (
                          <option key={p.fabricType} value={p.fabricType}>{p.fabricType}</option>
                        ))}
                      </optgroup>
                    )}
                    {fabricProducts.filter(p => p.category === 'insect').length > 0 && (
                      <optgroup label="Insect Screen">
                        {fabricProducts.filter(p => p.category === 'insect').map(p => (
                          <option key={p.fabricType} value={p.fabricType}>{p.fabricType}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div>
                  <label className="label text-xs">Fabric Color</label>
                  <select
                    value={fabricColor}
                    onChange={e => setFabricColor(e.target.value)}
                    disabled={!fabricType}
                    className="input-field w-full text-sm"
                  >
                    <option value="">Select Color...</option>
                    {fabricColorOptions.map(color => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Roll width info */}
              {selectedFabric && selectedFabric.rollWidth > 0 && (
                <div className="text-xs text-gray-500">
                  Roll width for {selectedFabric.fabricType}: {selectedFabric.rollWidth}"
                </div>
              )}

              {/* Size lines */}
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 px-1">
                  <div className="col-span-1">#</div>
                  <div className="col-span-3">Width (in)</div>
                  <div className="col-span-3">Height (in)</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2 text-right">Price</div>
                  <div className="col-span-1"></div>
                </div>
                {fabricLines.map((line, idx) => {
                  const w = parseFloat(line.width);
                  const h = parseFloat(line.height);
                  const linePrice = calcFabricLinePrice(w, h, line.windowPanel, line.windowCount);
                  const needsSeam = linePrice?.needsSeam;

                  return (
                    <div key={line.id}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-1 text-xs text-gray-500 text-center">{idx + 1}</div>
                        <div className="col-span-3">
                          <DimensionInput
                            value={line.width}
                            fractionMode={fractionMode}
                            onChange={(val) => {
                              setFabricLines(prev => prev.map(l => l.id === line.id ? { ...l, width: val } : l));
                            }}
                            placeholder={fractionMode ? 'e.g. 96 5/16' : '96'}
                            min="1"
                            className="input-field w-full text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <DimensionInput
                            value={line.height}
                            fractionMode={fractionMode}
                            onChange={(val) => {
                              setFabricLines(prev => prev.map(l => l.id === line.id ? { ...l, height: val } : l));
                            }}
                            placeholder={fractionMode ? 'e.g. 72 1/2' : '84'}
                            min="1"
                            className="input-field w-full text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            value={line.qty}
                            onChange={e => {
                              const val = parseInt(e.target.value) || 1;
                              setFabricLines(prev => prev.map(l => l.id === line.id ? { ...l, qty: val } : l));
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="1"
                            className="input-field w-full text-sm"
                          />
                        </div>
                        <div className="col-span-2 text-right text-sm">
                          {linePrice ? (
                            <span className="text-keder-yellow font-medium">${(linePrice.dealer * line.qty).toFixed(2)}</span>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </div>
                        <div className="col-span-1 text-center">
                          {fabricLines.length > 1 && (
                            <button
                              onClick={() => setFabricLines(prev => prev.filter(l => l.id !== line.id))}
                              className="text-red-400 hover:text-red-300 text-sm"
                              title="Remove line"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                      {needsSeam && (
                        <div className="ml-8 mt-1 text-xs text-yellow-400">
                          Seam required — height exceeds max panel cut
                        </div>
                      )}
                      {/* Window Panel option — only for panel replacement */}
                      {fabricOrderType === 'panel_replacement' && (
                        <div className="ml-8 mt-1 flex items-center gap-3">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={line.windowPanel}
                              onChange={e => setFabricLines(prev => prev.map(l =>
                                l.id === line.id ? { ...l, windowPanel: e.target.checked, windowCount: e.target.checked ? 1 : 0 } : l
                              ))}
                              className="rounded border-gray-600 bg-keder-darker text-keder-yellow focus:ring-keder-yellow"
                            />
                            <span className="text-xs text-gray-400">Window(s)</span>
                          </label>
                          {line.windowPanel && (
                            <input
                              type="number"
                              value={line.windowCount}
                              onChange={e => setFabricLines(prev => prev.map(l =>
                                l.id === line.id ? { ...l, windowCount: Math.max(1, parseInt(e.target.value) || 1) } : l
                              ))}
                              onWheel={(e) => e.currentTarget.blur()}
                              min="1"
                              max="20"
                              className="input-field w-16 text-xs text-center"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  onClick={() => setFabricLines(prev => [...prev, { id: String(Date.now()), width: '', height: '', qty: 1, windowPanel: false, windowCount: 1 }])}
                  className="text-teal-400 hover:text-teal-300 text-sm font-medium flex items-center gap-1 mt-1"
                >
                  <span className="text-lg leading-none">+</span> Add Line
                </button>
              </div>

              {/* Total price preview */}
              {fabricTotalPreview && (
                <div className="p-3 bg-keder-gray/30 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-400">
                      {fabricTotalPreview.lineCount} line{fabricTotalPreview.lineCount > 1 ? 's' : ''}
                    </div>
                    <div className="text-right">
                      <span className="text-keder-yellow font-bold text-lg">
                        ${fabricTotalPreview.totalDealer.toFixed(2)}
                      </span>
                      <span className="text-gray-500 text-xs ml-2 line-through">
                        ${fabricTotalPreview.totalMsrp.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={addFabricToCart}
                disabled={!fabricType || !fabricColor || !fabricLines.some(l => parseFloat(l.width) > 0 && parseFloat(l.height) > 0)}
                className="btn-primary w-full"
              >
                Add {fabricOrderType === 'panel_replacement' ? 'Panel' : 'Fabric'}{fabricLines.filter(l => parseFloat(l.width) > 0 && parseFloat(l.height) > 0).length > 1 ? 's' : ''} to Cart
              </button>
            </div>
            )}
          </div>

          {/* NEW DEALER STARTER KIT — purchasable now */}
          {apiSalesKit.filter(item => item.featured).map(item => {
            const stockStatus = getStockStatus(item.stock);
            return (
            <div key={item.id} className={`card ring-2 ${stockStatus.inStock ? 'ring-keder-yellow' : 'ring-orange-500'}`}>
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                {item.name}
                {stockStatus.inStock ? (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-keder-yellow text-black rounded-full font-medium">Available Now</span>
                ) : (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-orange-600 text-white rounded-full font-medium">Back Order</span>
                )}
              </h2>
              <div className="flex flex-col md:flex-row gap-6">
                {item.imageUrl && (
                  <div className="w-full md:w-64 flex-shrink-0">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-48 md:h-full object-cover rounded-lg"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-[var(--text-secondary)] mb-2">{item.description}</p>
                  <div className="flex items-center gap-3 mb-3">
                    <p className="text-sm text-[var(--text-muted)]">SKU: {item.sku}</p>
                    {stockStatus.label && (
                      <span className={`text-sm font-medium ${stockStatus.inStock ? 'text-green-400' : 'text-orange-400'}`}>
                        {stockStatus.inStock ? (
                          <>{stockStatus.label}</>
                        ) : (
                          <>Out of Stock &mdash; Back Order to Get on List</>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-keder-yellow font-bold text-2xl">{item.dealer > 0 ? `$${item.dealer.toFixed(2)}` : (item.sku === '42001' ? 'FREE — you only pay shipping' : 'Price on request')}</p>
                    <input
                      type="number"
                      value={salesKitQty[item.id] || 1}
                      onChange={e => setSalesKitQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="1"
                      className="w-16 input-field text-center"
                    />
                    <button
                      onClick={() => addSalesKitToCart(item)}
                      className={stockStatus.inStock ? 'btn-primary px-6' : 'px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors'}
                    >
                      {stockStatus.inStock ? 'Add to Cart' : 'Back Order'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })}

          {/* Assemblies — buy a complete assembly outright, no prior order.
              A damaged or mis-cut track needs the whole thing, not a list of
              components, so these are sold and priced as one unit. */}
          {asmCatalog.length > 0 && (
            <div className="card mb-6">
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Assemblies
              </h2>
              <p className="text-sm text-gray-400 mb-4">
                Complete assemblies built to your size — everything needed to fit one,
                including springs and hardware. Replacing a screen you bought from us?
                Use Reorder From a Past Order below and the sizes come across automatically.
              </p>

              {asmError && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-sm text-red-300">
                  {asmError}
                </div>
              )}

              <div className="space-y-3">
                {asmCatalog.map((a) => {
                  const f = asmField(a.code);
                  const q = asmQuote[a.code];
                  return (
                    <div key={a.code} className="p-4 bg-keder-darker rounded-lg">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[150px]">
                          <p className="text-white font-medium">{a.label}</p>
                          {q && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {q.components} components · ${q.subtotal.toFixed(2)}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            {a.lengthLabel}
                            {a.maxLengthInches && <span className="text-gray-600"> (max {a.maxLengthInches}")</span>}
                          </label>
                          <input
                            type="number" step="0.0625" min="1"
                            max={a.maxLengthInches}
                            value={f.length}
                            onChange={(e) => { setAsmField(a.code, { length: e.target.value }); setAsmQuote(prev => ({ ...prev, [a.code]: null })); }}
                            placeholder="inches"
                            className="input-field w-28 text-sm"
                          />
                        </div>

                        {a.hasSide && (
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Side</label>
                            <select
                              value={f.side}
                              onChange={(e) => { setAsmField(a.code, { side: e.target.value }); setAsmQuote(prev => ({ ...prev, [a.code]: null })); }}
                              className="select-field text-sm"
                            >
                              <option value="both">Both</option>
                              <option value="left">Left</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                        )}

                        {a.needsMotorSide && (
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Motor Side</label>
                            <select
                              value={f.motorSide}
                              onChange={(e) => { setAsmField(a.code, { motorSide: e.target.value }); setAsmQuote(prev => ({ ...prev, [a.code]: null })); }}
                              className="select-field text-sm"
                            >
                              <option value="right">Right</option>
                              <option value="left">Left</option>
                            </select>
                          </div>
                        )}

                        {a.mountOptions && (
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Mount</label>
                            <select
                              value={f.mount}
                              onChange={(e) => { setAsmField(a.code, { mount: e.target.value }); setAsmQuote(prev => ({ ...prev, [a.code]: null })); }}
                              className="select-field text-sm"
                            >
                              {(a.mountOptions === 'track_mount'
                                ? [['face', 'Face'], ['inside', 'Inside'], ['inside_face', 'Inside/Face'], ['inside_outside', 'Inside/Outside'], ['recessed', 'Recessed']]
                                : [['wall', 'Wall'], ['ceiling', 'Ceiling']]
                              ).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Color</label>
                          <select
                            value={f.color}
                            onChange={(e) => { setAsmField(a.code, { color: e.target.value }); setAsmQuote(prev => ({ ...prev, [a.code]: null })); }}
                            className="select-field text-sm"
                          >
                            {UCHANNEL_COLORS.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Qty</label>
                          <input
                            type="number" min="1"
                            value={f.qty}
                            onChange={(e) => { setAsmField(a.code, { qty: parseInt(e.target.value) || 1 }); setAsmQuote(prev => ({ ...prev, [a.code]: null })); }}
                            className="input-field w-20 text-sm"
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => quoteAssembly(a)}
                            disabled={asmBusy === a.code || !f.length}
                            className="px-3 py-2 border border-keder-gray text-gray-300 text-sm rounded-lg hover:border-keder-yellow disabled:opacity-50"
                          >
                            {asmBusy === a.code ? '…' : 'Price'}
                          </button>
                          <button
                            onClick={() => addCatalogAssemblyToCart(a)}
                            disabled={asmBusy === a.code || !f.length}
                            className="px-3 py-2 bg-keder-yellow text-black text-sm font-semibold rounded-lg disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* REPLACEMENT PARTS SECTION */}
          <div className="card relative overflow-hidden">
            <div className="absolute top-4 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wide z-10">Coming Soon</div>
            <div className="absolute inset-0 bg-keder-dark/40 z-[5] pointer-events-auto" />
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Replacement Parts
            </h2>

            {/* Search */}
            <div className="mb-6">
              <input
                type="text"
                value={partsSearch}
                onChange={e => setPartsSearch(e.target.value)}
                placeholder="Search by part number or name..."
                className="input-field w-full"
              />
            </div>

            {/* Parts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
              {filteredParts.map(part => (
                <div key={part.id} className="bg-keder-darker rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{part.name}</p>
                    <p className="text-sm text-gray-400">SKU: {part.sku}</p>
                    <p className="text-keder-yellow font-medium mt-1">
                      ${part.dealer.toFixed(2)}
                      {part.unit && <span className="text-gray-400 text-xs ml-1">{part.unit}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={partsQty[part.id] || 1}
                      onChange={e => setPartsQty(prev => ({ ...prev, [part.id]: parseInt(e.target.value) || 1 }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="1"
                      className="w-16 input-field text-center"
                    />
                    <button
                      onClick={() => addPartToCart(part)}
                      className="btn-secondary px-3 py-2"
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Replacement Parts — order from a previous screen order.
              Cut lengths come off the order that was actually built, so
              nothing has to be re-measured on a wall. */}
          <div className="card mb-6">
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Replacement Parts
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Order parts for a screen you've already bought. Sizes come from the original
              order — no need to measure anything again.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                type="text"
                value={replOrderNumber}
                onChange={(e) => setReplOrderNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') lookupReplacementOrder(); }}
                placeholder="Order number, e.g. VT26080014"
                className="input-field flex-1"
              />
              <button
                onClick={lookupReplacementOrder}
                disabled={replLoading || !replOrderNumber.trim()}
                className="px-4 py-2 bg-keder-yellow text-black font-semibold rounded-lg disabled:opacity-50"
              >
                {replLoading ? 'Looking up…' : 'Find Order'}
              </button>
            </div>

            {replError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-sm text-red-300">
                {replError}
              </div>
            )}

            {replOrder && (
              <div className="space-y-4">
                <div className="text-sm text-gray-300">
                  <span className="font-semibold text-white">{replOrder.orderNumber}</span>
                  {replOrder.sidemark && <span className="text-gray-400"> — {replOrder.sidemark}</span>}
                  <span className="text-gray-500"> · {new Date(replOrder.createdAt).toLocaleDateString()}</span>
                </div>

                {/* Pre-regeneration orders carry recalculated lengths, not
                    necessarily what was cut. Say so rather than let someone
                    cut a track to a number that might be wrong. */}
                {replOrder.lengthsMayDiffer && (
                  <div className="p-3 bg-amber-900/30 border border-amber-700 rounded-lg text-sm text-amber-200">
                    {replOrder.lengthsWarning}
                  </div>
                )}

                {replOrder.screens.length > 1 && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Which screen?</label>
                    <div className="flex flex-wrap gap-2">
                      {replOrder.screens.map((s: any) => (
                        <button
                          key={s.itemNumber}
                          onClick={() => setReplScreen(s.itemNumber)}
                          className={clsx(
                            'px-3 py-2 rounded-lg text-sm border',
                            replScreen === s.itemNumber
                              ? 'bg-keder-yellow text-black border-keder-yellow font-semibold'
                              : 'bg-keder-darker text-gray-300 border-keder-gray hover:border-gray-500',
                          )}
                        >
                          #{s.itemNumber} · {s.width}" × {s.height}"
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {replOrder.screens.length === 0 && (
                  <div className="p-3 bg-keder-darker border border-keder-gray rounded-lg text-sm text-gray-400">
                    This order has no screens on it, so there are no parts to reorder.
                  </div>
                )}

                {replOrder.screens.length > 0 && replOrder.hasBom === false && (
                  <div className="p-3 bg-amber-900/30 border border-amber-700 rounded-lg text-sm text-amber-200">
                    This order was never released to production, so individual parts aren't
                    listed. Complete assemblies are still available below.
                  </div>
                )}

                {replOrder.screens.length > 1 && replScreen === null && (
                  <p className="text-sm text-gray-400">Pick a screen above to see what can be reordered.</p>
                )}

                {currentReplScreen() && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-2">Assemblies</h3>
                      <div className="space-y-2">
                        {currentReplScreen().assemblies.map((asm: any) => {
                          const key = `${currentReplScreen().itemNumber}-${asm.code}`;
                          return (
                            <div key={asm.code} className="flex flex-wrap items-center gap-3 p-3 bg-keder-darker rounded-lg">
                              <div className="flex-1 min-w-[180px]">
                                <p className="text-white text-sm font-medium">{asm.label}</p>
                                <p className="text-xs text-gray-400">
                                  {asm.code === 'fabric_panel'
                                    ? `${currentReplScreen().width}" × ${currentReplScreen().height}" — from the original order`
                                    : `${asm.lengthInches}" — from the original order`}
                                </p>
                                {/* Says plainly that the panel arrives made up, so
                                    nobody goes looking for the pieces. */}
                                {asm.note && <p className="text-xs text-gray-500 mt-0.5">{asm.note}</p>}
                              </div>
                              {asm.needsSide && (
                                <select
                                  value={replSide[key] || 'both'}
                                  onChange={(e) => setReplSide(prev => ({ ...prev, [key]: e.target.value as any }))}
                                  className="select-field text-sm"
                                >
                                  <option value="both">Both sides</option>
                                  <option value="left">Left only</option>
                                  <option value="right">Right only</option>
                                </select>
                              )}
                              <button
                                onClick={() => addAssemblyToCart(asm)}
                                disabled={replAdding === key}
                                className="px-3 py-2 bg-keder-yellow text-black text-sm font-semibold rounded-lg disabled:opacity-50"
                              >
                                {replAdding === key ? 'Pricing…' : 'Add'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-white mb-2">
                        Individual Parts
                        <span className="text-gray-500 font-normal"> ({currentReplScreen().parts.length})</span>
                      </h3>
                      {currentReplScreen().parts.length === 0 && (
                        <p className="text-sm text-gray-500 italic">
                          No individual parts on record for this screen — use the assemblies above.
                        </p>
                      )}
                      <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
                        {currentReplScreen().parts.map((part: any) => {
                          const key = `${currentReplScreen().itemNumber}-${part.partNumber}`;
                          return (
                            <div key={part.partNumber} className="flex items-center gap-3 p-2 bg-keder-darker rounded text-sm">
                              <div className="flex-1 min-w-0">
                                <p className="text-gray-200 truncate">{part.description}</p>
                                <p className="text-xs text-gray-500">
                                  {part.partNumber}
                                  {part.cutLength ? ` · cut ${part.cutLength}"` : ''}
                                </p>
                              </div>
                              <button
                                onClick={() => addReplacementPartToCart(part)}
                                disabled={replAdding === key}
                                className="px-2 py-1 border border-keder-gray text-gray-300 text-xs rounded hover:border-keder-yellow disabled:opacity-50"
                              >
                                {replAdding === key ? '…' : 'Add'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* SALES KIT SECTION — Coming Soon */}
          <div className="card relative overflow-hidden">
            <div className="absolute top-4 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wide z-10">Coming Soon</div>
            <div className="absolute inset-0 bg-keder-dark/40 z-[5] pointer-events-auto" />
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Sales Kit
            </h2>

            <div className="mb-4 p-3 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-500 rounded-lg text-sm text-purple-800 dark:text-purple-200">
              <strong>For Dealers:</strong> Get equipped to sell V-Track screens with professional materials, fabric samples, and marketing kits.
            </div>

            {/* Trade Show Kit (non-featured kits only) */}
            <h3 className="text-lg font-semibold text-white mb-4">Trade Show & Marketing Kits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {apiSalesKit.filter(item => item.category === 'kit' && !item.featured).map(item => (
                <div key={item.id} className="bg-keder-darker rounded-lg p-4">
                  <p className="text-white font-medium">{item.name}</p>
                  <p className="text-sm text-gray-400 mt-1">{item.description}</p>
                  <p className="text-sm text-gray-500 mt-1">SKU: {item.sku}</p>
                  <p className="text-keder-yellow font-medium mt-2">
                    {item.dealer > 0 ? `$${item.dealer.toFixed(2)}` : 'Price on request'}                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="number"
                      value={salesKitQty[item.id] || 1}
                      onChange={e => setSalesKitQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="1"
                      className="w-16 input-field text-center"
                    />
                    <button
                      onClick={() => addSalesKitToCart(item)}
                      className="btn-primary flex-1"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Fabric Samples */}
            <h3 className="text-lg font-semibold text-white mb-4">Fabric Samples</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {apiSalesKit.filter(item => item.category === 'sample').map(item => (
                <div key={item.id} className="bg-keder-darker rounded-lg p-4">
                  <p className="text-white font-medium">{item.name}</p>
                  <p className="text-sm text-gray-400">{item.description}</p>
                  <p className="text-keder-yellow font-medium mt-2">
                    {item.dealer > 0 ? `$${item.dealer.toFixed(2)}` : 'Price on request'}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="number"
                      value={salesKitQty[item.id] || 1}
                      onChange={e => setSalesKitQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="1"
                      className="w-16 input-field text-center"
                    />
                    <button
                      onClick={() => addSalesKitToCart(item)}
                      className="btn-secondary px-3 py-2"
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Marketing & Technical Materials */}
            <h3 className="text-lg font-semibold text-white mb-4">Marketing & Technical Materials</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {apiSalesKit.filter(item => item.category === 'marketing' || item.category === 'technical').map(item => (
                <div key={item.id} className="bg-keder-darker rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{item.name}</p>
                    <p className="text-sm text-gray-400">{item.description}</p>
                    <p className="text-keder-yellow font-medium mt-1">
                      {item.dealer > 0 ? `$${item.dealer.toFixed(2)}` : 'Price on request'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={salesKitQty[item.id] || 1}
                      onChange={e => setSalesKitQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="1"
                      className="w-16 input-field text-center"
                    />
                    <button
                      onClick={() => addSalesKitToCart(item)}
                      className="btn-secondary px-3 py-2"
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* DEMO UNITS SECTION */}
          <div className="card relative overflow-hidden">
            <div className="absolute top-4 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wide z-10">Coming Soon</div>
            <div className="absolute inset-0 bg-keder-dark/40 z-[5] pointer-events-auto" />
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Demo Units
              <span className="ml-2 px-2 py-0.5 text-xs bg-orange-600 text-white rounded-full">Trade Shows & Showrooms</span>
            </h2>

            <div className="mb-4 p-3 bg-orange-900/30 border border-orange-500 rounded-lg text-sm text-orange-200">
              <strong>Show Off V-Track:</strong> Demo units for trade shows, showrooms, and customer presentations. Special dealer pricing on fully functional display screens.
            </div>

            {/* Tabletop Demos */}
            <h3 className="text-lg font-semibold text-white mb-4">Tabletop Demo Units</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {apiDemoUnits.filter(item => item.category === 'tabletop').map(item => {
                const stockStatus = getStockStatus(item.stock);
                return (
                  <div key={item.id} className={`bg-keder-darker rounded-lg p-4 ${!stockStatus.inStock ? 'border border-orange-500/50' : ''}`}>
                    <div className="flex justify-between items-start">
                      <p className="text-white font-medium">{item.name}</p>
                      {!stockStatus.inStock && (
                        <span className="px-2 py-0.5 text-xs bg-orange-600 text-white rounded-full">Backorder</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{item.description}</p>
                    <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                    {stockStatus.label && (
                      <p className={`text-sm mt-1 ${stockStatus.inStock ? 'text-green-400' : 'text-orange-400'}`}>
                        {stockStatus.label}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="number"
                        value={demoQty[item.id] || 1}
                        onChange={e => setDemoQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min="1"
                        className="w-16 input-field text-center"
                      />
                      <button
                        onClick={() => addDemoToCart(item)}
                        className={`flex-1 px-3 py-2 rounded-lg font-medium ${!stockStatus.inStock ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'btn-secondary'}`}
                      >
                        {!stockStatus.inStock ? 'Pre-Order' : 'Add'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Freestanding Demos */}
            <h3 className="text-lg font-semibold text-white mb-4">Freestanding Demo Units</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {apiDemoUnits.filter(item => item.category === 'freestanding').map(item => {
                const stockStatus = getStockStatus(item.stock);
                return (
                  <div key={item.id} className={`bg-keder-darker rounded-lg p-4 ${!stockStatus.inStock ? 'border border-orange-500/50' : ''}`}>
                    <div className="flex justify-between items-start">
                      <p className="text-white font-medium">{item.name}</p>
                      {!stockStatus.inStock && (
                        <span className="px-2 py-0.5 text-xs bg-orange-600 text-white rounded-full">Backorder</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{item.description}</p>
                    <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                    {stockStatus.label && (
                      <p className={`text-sm mt-1 ${stockStatus.inStock ? 'text-green-400' : 'text-orange-400'}`}>
                        {stockStatus.label}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="number"
                        value={demoQty[item.id] || 1}
                        onChange={e => setDemoQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min="1"
                        className="w-16 input-field text-center"
                      />
                      <button
                        onClick={() => addDemoToCart(item)}
                        className={`flex-1 px-3 py-2 rounded-lg font-medium ${!stockStatus.inStock ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'btn-secondary'}`}
                      >
                        {!stockStatus.inStock ? 'Pre-Order' : 'Add'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Showroom Demos */}
            <h3 className="text-lg font-semibold text-white mb-4">Showroom Demo Screens</h3>
            <div className="grid grid-cols-1 gap-4 mb-6">
              {apiDemoUnits.filter(item => item.category === 'showroom').map(item => {
                const stockStatus = getStockStatus(item.stock);
                return (
                  <div key={item.id} className={`bg-keder-darker rounded-lg p-4 ${item.featured ? 'ring-2 ring-keder-yellow' : ''} ${!stockStatus.inStock ? 'border border-orange-500/50' : ''}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium text-lg">{item.name}</p>
                          {item.featured && (
                            <span className="px-2 py-0.5 text-xs bg-keder-yellow text-black rounded-full font-medium">Premium</span>
                          )}
                          {!stockStatus.inStock && (
                            <span className="px-2 py-0.5 text-xs bg-orange-600 text-white rounded-full">Backorder</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{item.description}</p>
                        <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                        {stockStatus.label && (
                          <p className={`text-sm mt-1 ${stockStatus.inStock ? 'text-green-400' : 'text-orange-400'}`}>
                            {stockStatus.label}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={demoQty[item.id] || 1}
                          onChange={e => setDemoQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                          onWheel={(e) => e.currentTarget.blur()}
                          min="1"
                          className="w-16 input-field text-center"
                        />
                        <button
                          onClick={() => addDemoToCart(item)}
                          className={`px-6 py-2 rounded-lg font-medium ${!stockStatus.inStock ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'btn-primary'}`}
                        >
                          {!stockStatus.inStock ? 'Pre-Order' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Demo Accessories */}
            <h3 className="text-lg font-semibold text-white mb-4">Demo Accessories</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {apiDemoUnits.filter(item => item.category === 'accessory').map(item => {
                const stockStatus = getStockStatus(item.stock);
                return (
                  <div key={item.id} className={`bg-keder-darker rounded-lg p-4 ${!stockStatus.inStock ? 'border border-orange-500/50' : ''}`}>
                    <div className="flex justify-between items-start">
                      <p className="text-white font-medium">{item.name}</p>
                      {!stockStatus.inStock && (
                        <span className="px-2 py-0.5 text-xs bg-orange-600 text-white rounded-full">Backorder</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{item.description}</p>
                    {stockStatus.label && (
                      <p className={`text-sm mt-1 ${stockStatus.inStock ? 'text-green-400' : 'text-orange-400'}`}>
                        {stockStatus.label}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="number"
                        value={demoQty[item.id] || 1}
                        onChange={e => setDemoQty(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 1 }))}
                        onWheel={(e) => e.currentTarget.blur()}
                        min="1"
                        className="w-16 input-field text-center"
                      />
                      <button
                        onClick={() => addDemoToCart(item)}
                        className={`flex-1 px-3 py-2 rounded-lg font-medium ${!stockStatus.inStock ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'btn-secondary'}`}
                      >
                        {!stockStatus.inStock ? 'Pre-Order' : 'Add'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ACCESSORIES SECTION */}
          <div className="card relative overflow-hidden">
            <div className="absolute top-4 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wide z-10">Coming Soon</div>
            <div className="absolute inset-0 bg-keder-dark/40 z-[5] pointer-events-auto" />
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Accessories
            </h2>

            {apiAccessories.length === 0 ? (
              <p className="text-gray-400 text-center py-4">Loading accessories...</p>
            ) : (
              <>
                {/* Group by brand */}
                {[...new Set(apiAccessories.filter(a => a.category !== 'sensor').map(a => a.brand))].map(brand => (
                  <div key={brand} className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">{brand} Remotes & Switches</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {apiAccessories.filter(a => a.brand === brand && a.category !== 'sensor').map(acc => (
                        <div key={acc.id} className="bg-keder-darker rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{acc.name}</p>
                            <p className="text-sm text-gray-400">SKU: {acc.sku}</p>
                            <p className="text-keder-yellow font-medium mt-1">
                              ${acc.dealer.toFixed(2)}
                                                          </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={accessoryQty[acc.id] || 1}
                              onChange={e => setAccessoryQty(prev => ({ ...prev, [acc.id]: parseInt(e.target.value) || 1 }))}
                              onWheel={(e) => e.currentTarget.blur()}
                              min="1"
                              className="w-16 input-field text-center"
                            />
                            <button
                              onClick={() => addAccessoryToCart(acc)}
                              className="btn-secondary px-3 py-2"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Sensors */}
                {apiAccessories.filter(a => a.category === 'sensor').length > 0 && (
                  <>
                    <h3 className="text-lg font-semibold text-white mb-4">Wind & Sun Sensors</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {apiAccessories.filter(a => a.category === 'sensor').map(acc => (
                        <div key={acc.id} className="bg-keder-darker rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{acc.name}</p>
                            <p className="text-sm text-gray-400">SKU: {acc.sku}</p>
                            <p className="text-keder-yellow font-medium mt-1">
                              ${acc.dealer.toFixed(2)}
                                                          </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={accessoryQty[acc.id] || 1}
                              onChange={e => setAccessoryQty(prev => ({ ...prev, [acc.id]: parseInt(e.target.value) || 1 }))}
                              onWheel={(e) => e.currentTarget.blur()}
                              min="1"
                              className="w-16 input-field text-center"
                            />
                            <button
                              onClick={() => addAccessoryToCart(acc)}
                              className="btn-secondary px-3 py-2"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>


        </div>

        {/* CART SIDEBAR */}
        <div className="lg:col-span-1">
          <div className="card sticky top-4">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <svg className="w-6 h-6 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Cart
              </span>
              <span className="bg-keder-yellow text-black px-2 py-1 rounded-full text-sm font-bold">
                {cart.length}
              </span>
            </h2>

            {editingOrderId && editingOrderNumber && (
              <div className="bg-blue-900/30 border border-blue-500/50 text-blue-200 px-3 py-2 rounded-lg mb-4 text-sm">
                Editing existing order <strong>{editingOrderNumber}</strong>. Changes will update this order.
              </div>
            )}

            {error && (
              <div className="bg-red-100 dark:bg-red-900/50 border border-red-300 dark:border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-100 dark:bg-green-900/50 border border-green-300 dark:border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">
                {success}
              </div>
            )}

            {cart.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Your cart is empty</p>
            ) : (
              <>
                {/* Cart Items */}
                <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                  {cart.map(item => (
                    <div key={item.id} className="bg-keder-darker rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{item.name}</p>
                          {item.description && (
                            <p className="text-gray-400 text-xs">{item.description}</p>
                          )}
                          {item.sku && (
                            <p className="text-gray-500 text-xs">SKU: {item.sku}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-gray-400 hover:text-red-400 ml-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartQty(item.id, item.quantity - 1)}
                            className="text-gray-400 hover:text-white"
                          >
                            -
                          </button>
                          <span className="text-white w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQty(item.id, item.quantity + 1)}
                            className="text-gray-400 hover:text-white"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-keder-yellow font-medium">
                          ${(item.unitPrice * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Shipping */}
                <div className="border-t border-keder-gray pt-4 mb-4">
                  <h3 className="text-white font-medium mb-3">Shipping</h3>
                  {shippingMethod === 'ship' && <ShippingDisclaimer className="mb-3" />}

                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => {
                        setShippingMethod('ship');
                        setFreightInfo(null);
                        setParcelQuotes([]);
                        setSelectedParcelQuote(null);
                        setCarrierType(null);
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        shippingMethod === 'ship'
                          ? 'bg-keder-yellow text-black'
                          : 'bg-keder-darker text-gray-300 hover:bg-keder-gray'
                      }`}
                    >
                      Ship to Me
                    </button>
                    <button
                      onClick={() => {
                        setShippingMethod('willcall');
                        setFreightInfo(null);
                        setFreightError('');
                        setParcelQuotes([]);
                        setSelectedParcelQuote(null);
                        setCarrierType(null);
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        shippingMethod === 'willcall'
                          ? 'bg-keder-yellow text-black'
                          : 'bg-keder-darker text-gray-300 hover:bg-keder-gray'
                      }`}
                    >
                      Will Call ($0)
                    </button>
                  </div>

                  {shippingMethod === 'ship' && (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={shipStreet}
                        onChange={e => {
                          setShipStreet(e.target.value);
                          setPoBoxWarning(checkPOBox(e.target.value));
                        }}
                        placeholder="Street Address"
                        className="input-field w-full text-sm"
                      />
                      {poBoxWarning && (
                        <div className="bg-yellow-900/30 border border-yellow-500 rounded-lg p-3 text-sm text-yellow-300">
                          <strong>PO Box Notice:</strong> UPS does not deliver to PO Boxes. To ship to a PO Box, enter the street address of the post office with your box number as the apartment/suite number. USPS and FedEx can deliver to PO Boxes.
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={shipCity}
                          onChange={e => setShipCity(e.target.value)}
                          placeholder="City"
                          className="input-field text-sm"
                        />
                        <input
                          type="text"
                          value={shipState}
                          onChange={e => setShipState(e.target.value)}
                          placeholder="State"
                          className="input-field text-sm"
                        />
                      </div>
                      <input
                        type="text"
                        value={shipZip}
                        onChange={e => setShipZip(e.target.value)}
                        placeholder="ZIP Code"
                        className="input-field w-full text-sm"
                        maxLength={5}
                      />

                      <div className="flex flex-wrap gap-4 text-sm">
                        <label className="flex items-center gap-2 text-gray-300">
                          <input
                            type="checkbox"
                            checked={liftgate}
                            onChange={e => setLiftgate(e.target.checked)}
                            className="rounded border-gray-600"
                          />
                          Liftgate
                        </label>
                        <label className="flex items-center gap-2 text-gray-300">
                          <input
                            type="checkbox"
                            checked={residential}
                            onChange={e => setResidential(e.target.checked)}
                            className="rounded border-gray-600"
                          />
                          Residential
                        </label>
                      </div>

                      {/* Get Shipping Quote Button — pulses when no quote selected */}
                      <button
                        onClick={getShippingQuote}
                        disabled={freightLoading || !shipZip || shipZip.length !== 5}
                        className={`w-full font-semibold py-3 px-4 rounded-lg transition-all ${
                          (freightInfo || selectedParcelQuote)
                            ? 'btn-secondary'
                            : 'bg-yellow-500 hover:bg-yellow-400 text-black animate-pulse shadow-lg shadow-yellow-500/40 text-base'
                        }`}
                      >
                        {freightLoading ? 'Getting Quote...' : (freightInfo || selectedParcelQuote) ? 'Update Shipping Quote' : 'Get Shipping Quote'}
                      </button>

                      {freightError && (
                        <p className="text-red-400 text-sm">{freightError}</p>
                      )}

                      {/* Small Package Carrier Quotes */}
                      {parcelQuotes.length > 0 && !freightLoading && (
                        <div className="space-y-2">
                          <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">
                            {carrierType === 'mixed' ? 'Small Package Shipping' : 'Shipping Options'}
                          </p>
                          {parcelQuotes.map((quote, idx) => (
                            <label
                              key={`${quote.carrier}-${quote.serviceCode}`}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedParcelQuote?.serviceCode === quote.serviceCode && selectedParcelQuote?.carrier === quote.carrier
                                  ? 'border-keder-yellow bg-keder-yellow/10'
                                  : 'border-keder-gray bg-keder-darker hover:border-gray-500'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="parcelQuote"
                                  checked={selectedParcelQuote?.serviceCode === quote.serviceCode && selectedParcelQuote?.carrier === quote.carrier}
                                  onChange={() => setSelectedParcelQuote(quote)}
                                  className="accent-keder-yellow"
                                />
                                <div>
                                  <span className="text-white text-sm">{quote.serviceName}</span>
                                  <p className="text-gray-400 text-xs">{quote.transitDescription}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-white font-medium text-sm">${quote.totalAmount.toFixed(2)}</span>
                                {idx === 0 && <p className="text-green-400 text-xs">Lowest Price</p>}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* LTL Freight Quotes */}
                      {freightQuotes.length > 0 && !freightLoading && (
                        <div className="space-y-2">
                          <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">
                            {carrierType === 'mixed' ? 'Freight Shipping (Oversized Items)' : 'Freight Options'}
                          </p>
                          {freightQuotes.map((fq, idx) => (
                            <label
                              key={fq.quoteId}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                freightInfo?.selectedQuoteId === fq.quoteId
                                  ? 'border-keder-yellow bg-keder-yellow/10'
                                  : 'border-keder-gray bg-keder-darker hover:border-gray-500'
                              }`}
                              onClick={() => setFreightInfo({
                                carrier: fq.carrierName,
                                service: fq.serviceDescription,
                                transitTime: fq.transitTime,
                                rate: fq.totalAmount,
                                freightviewShipmentId: freightInfo?.freightviewShipmentId,
                                selectedQuoteId: fq.quoteId,
                                selectedCarrierCode: fq.carrierCode,
                              })}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="freightQuote"
                                  checked={freightInfo?.selectedQuoteId === fq.quoteId}
                                  readOnly
                                  className="accent-keder-yellow"
                                />
                                <div>
                                  <span className="text-white text-sm">{fq.carrierName} - {fq.serviceDescription}</span>
                                  <p className="text-gray-400 text-xs">{fq.transitTime}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-white font-medium text-sm">${fq.totalAmount.toFixed(2)}</span>
                                {idx === 0 && <p className="text-green-400 text-xs">Lowest Price</p>}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                      {/* Fallback: single freight quote (no allQuotes) */}
                      {freightInfo && freightQuotes.length === 0 && !freightLoading && (
                        <div className="space-y-2">
                          <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-500 rounded-lg p-3 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-300">{freightInfo.carrier} - {freightInfo.service}</span>
                              <span className="text-white font-medium">${freightInfo.rate.toFixed(2)}</span>
                            </div>
                            {freightInfo.transitTime && (
                              <p className="text-gray-400 text-xs mt-1">{freightInfo.transitTime}</p>
                            )}
                            {freightInfo.fallback && (
                              <p className="text-yellow-400 text-xs mt-1">Estimated rate - actual may vary</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {shippingMethod === 'willcall' && (
                    <div className="bg-keder-darker rounded-lg p-3 text-sm">
                      <p className="text-white font-medium">Pickup Location</p>
                      <p className="text-gray-400">12446 State Hwy 205</p>
                      <p className="text-gray-400">Lavon, TX 75166</p>
                      <p className="text-gray-500 text-xs mt-2">Mon-Fri 8am-5pm CT</p>
                    </div>
                  )}
                </div>

                {/* Sidemark / PO — optional reference fields */}
                <div className="space-y-2 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Sidemark / Job Name
                    </label>
                    <input
                      type="text"
                      value={sidemark}
                      onChange={(e) => setSidemark(e.target.value)}
                      placeholder="e.g. Smith Residence"
                      maxLength={100}
                      className="w-full px-3 py-2 bg-keder-darker border border-keder-gray rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-keder-yellow"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      PO Number
                    </label>
                    <input
                      type="text"
                      value={poNumber}
                      onChange={(e) => setPoNumber(e.target.value)}
                      placeholder="Optional"
                      maxLength={50}
                      className="w-full px-3 py-2 bg-keder-darker border border-keder-gray rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-keder-yellow"
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-keder-gray pt-4 space-y-2">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span>${cartSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Shipping</span>
                    <span>
                      {shippingMethod === 'willcall'
                        ? '$0.00'
                        : (selectedParcelQuote || freightInfo)
                          ? `$${(
                              (selectedParcelQuote?.totalAmount || 0) +
                              (carrierType === 'mixed' ? (freightInfo?.rate || 0) : (carrierType === 'ltl_freight' ? (freightInfo?.rate || 0) : 0))
                            ).toFixed(2)}`
                          : '--'
                      }
                    </span>
                  </div>
                  {taxPreview > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Sales Tax <span className="text-gray-500 text-xs">({taxRatePct.toFixed(2)}%)</span></span>
                      <span>${taxPreview.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-900 dark:text-white font-bold text-lg pt-2 border-t border-keder-gray">
                    <span>Total</span>
                    <span className="text-green-600 dark:text-green-400">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Error / Success Messages */}
                {error && !showCardModal && (
                  <div className="mt-4 text-red-700 dark:text-red-400 text-sm bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-500/30 rounded-lg p-3">{error}</div>
                )}
                {success && (
                  <div className="mt-4 text-green-700 dark:text-green-400 text-sm bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-500/30 rounded-lg p-3">{success}</div>
                )}

                {/* Checkout Buttons — Stripe CC + ACH */}
                <div className="space-y-3 mt-6">
                  <div className="flex gap-2">
                    <button
                      onClick={() => { if (cart.length > 0) setCardConsentOpen(true); }}
                      disabled={processing || cart.length === 0 || (shippingMethod === 'ship' && !freightInfo && !selectedParcelQuote)}
                      className="flex-1 py-3 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: '#635bff' }}
                    >
                      {processing ? 'Processing...' : `Pay with Card ($${cartTotal.toFixed(2)})`}
                    </button>
                    <button
                      onClick={() => handleCheckout('us_bank_account')}
                      disabled={processing || cart.length === 0 || (shippingMethod === 'ship' && !freightInfo && !selectedParcelQuote)}
                      className="flex-1 py-3 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: '#16a34a' }}
                      title={achDiscountPercent > 0 ? `Save ${achDiscountPercent}% by paying with bank account` : undefined}
                    >
                      {processing ? 'Processing...' : `Pay with Bank ($${(cartTotal * (1 - achDiscountPercent / 100)).toFixed(2)})`}
                      {achDiscountPercent > 0 && <span className="ml-1 text-xs opacity-75">-{achDiscountPercent}%</span>}
                    </button>
                  </div>

                  {/* Card fee consent → hosted Stripe checkout (Link/Apple/Google Pay) */}
                  <CardFeeConsentModal
                    open={cardConsentOpen}
                    amount={cartTotal}
                    onClose={() => setCardConsentOpen(false)}
                    onAgree={() => { setCardConsentOpen(false); handleCheckout('card'); }}
                  />

                  {/* Authorize.net / Helcim removed — all payments via Stripe */}

                  {/* Submit for Invoice — subtle link.
                      In edit mode, this becomes the "save changes without paying" action. */}
                  <div className="text-center mt-2">
                    <button
                      onClick={() => editingOrderId ? handleSubmitForInvoice() : setShowInvoiceConfirm(true)}
                      disabled={processing || cardProcessing || cart.length === 0 || (shippingMethod === 'ship' && !freightInfo && !selectedParcelQuote)}
                      className="text-sm text-gray-400 hover:text-gray-200 underline disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {editingOrderId ? 'Save Changes (no payment)' : 'Submit for Invoicing (wire transfer / QB)'}
                    </button>
                  </div>

                  {/* Invoice Confirmation Modal */}
                  {showInvoiceConfirm && (
                    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowInvoiceConfirm(false)}>
                      <div className="bg-gray-900 border-2 border-yellow-500/50 rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-bold text-white">Are you sure?</h3>
                        </div>
                        <p className="text-gray-300 text-sm mb-3">
                          The <strong className="text-white">Submit for Invoicing</strong> button is only for dealers who pay by <strong className="text-white">wire transfer</strong> or receive invoices through <strong className="text-white">QuickBooks</strong>.
                        </p>
                        <p className="text-gray-300 text-sm mb-3">
                          Orders submitted for invoicing are <strong className="text-yellow-400">not processed until payment is received</strong>, which may delay your order.
                        </p>
                        <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3 mb-6">
                          <p className="text-green-400 text-sm font-medium">
                            For faster processing, go back and use the payment buttons above to pay instantly with credit card or ACH bank transfer.
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowInvoiceConfirm(false)}
                            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                          >
                            Go Back & Pay Now
                          </button>
                          <button
                            onClick={handleSubmitForInvoice}
                            className="flex-1 px-4 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 font-medium text-sm"
                          >
                            Proceed with Invoicing
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {shippingMethod === 'ship' && !freightInfo && cart.length > 0 && (
                  <p className="text-yellow-400 text-xs text-center mt-2">
                    Click "Get Shipping Quote" before checkout
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Authorize.net Card Payment Modal */}
      {showCardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="light-modal bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Pay ${cartTotal.toFixed(2)}</h3>
              <button onClick={() => setShowCardModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {/* Order Summary */}
            <div className="mb-4 bg-gray-50 rounded-lg p-3 text-sm max-h-40 overflow-y-auto border border-gray-200">
              {cart.map((item, idx) => (
                <div key={item.id || idx} className="flex justify-between py-1 border-b border-gray-200 last:border-0">
                  <span className="text-gray-700 truncate mr-2">
                    {item.name}{item.color ? ` - ${item.color}` : ''}{item.lengthInches ? ` ${item.lengthInches}"` : ''}
                    {item.quantity > 1 ? ` x${item.quantity}` : ''}
                  </span>
                  <span className="text-gray-900 font-medium whitespace-nowrap">${(item.unitPrice * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              {shippingMethod !== 'willcall' && (cartSubtotal !== cartTotal) && (
                <div className="flex justify-between py-1 text-gray-500">
                  <span>Shipping</span>
                  <span>${(cartTotal - cartSubtotal).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-1 mt-1 border-t border-gray-300 font-bold text-gray-900">
                <span>Total</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Tabs: New Card / Bank Account / Saved Methods */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setCardModalTab('card')}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cardModalTab === 'card' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >Card</button>
              <button
                onClick={() => setCardModalTab('bank')}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cardModalTab === 'bank' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >Bank (ACH)</button>
              <button
                onClick={() => setCardModalTab('saved')}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                  cardModalTab === 'saved' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >Saved {savedMethods.length > 0 && `(${savedMethods.length})`}</button>
            </div>

            {error && <div className="mb-3 text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2">{error}</div>}

            {/* Tab: New Card */}
            {cardModalTab === 'card' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                  <input
                    type="text"
                    value={cardForm.cardNumber}
                    onChange={e => setCardForm({ ...cardForm, cardNumber: e.target.value.replace(/[^\d\s]/g, '') })}
                    placeholder="4111 1111 1111 1111"
                    maxLength={19}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <input
                      type="text"
                      value={cardForm.expMonth}
                      onChange={e => setCardForm({ ...cardForm, expMonth: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                      placeholder="MM"
                      maxLength={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <input
                      type="text"
                      value={cardForm.expYear}
                      onChange={e => setCardForm({ ...cardForm, expYear: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      placeholder="YYYY"
                      maxLength={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                    <input
                      type="text"
                      value={cardForm.cvv}
                      onChange={e => setCardForm({ ...cardForm, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      placeholder="123"
                      maxLength={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={cardForm.saveCard}
                    onChange={e => setCardForm({ ...cardForm, saveCard: e.target.checked })}
                    className="accent-blue-500"
                  />
                  Save card for future orders
                </label>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    By clicking "Pay" below, I authorize International Shade Products Inc to charge my credit or debit card <strong>${cartTotal.toFixed(2)}</strong> on
                    or after <strong>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong> for
                    this parts order.
                  </p>
                </div>
                <button
                  onClick={handleAuthorizeNetPayment}
                  disabled={cardProcessing || !cardForm.cardNumber || !cardForm.expMonth || !cardForm.expYear || !cardForm.cvv}
                  className="w-full py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 bg-green-600 hover:bg-green-700"
                >
                  {cardProcessing ? 'Processing...' : `Pay $${cartTotal.toFixed(2)}`}
                </button>
              </div>
            )}

            {/* Tab: Bank Account (ACH via HelcimPay.js) */}
            {cardModalTab === 'bank' && (
              <div className="space-y-3">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium mb-2">Secure ACH Bank Payment</p>
                  <p className="text-xs text-blue-700">
                    Click the button below to open a secure payment form powered by Helcim.
                    You'll enter your bank account details directly on Helcim's secure form — your banking information is never stored on our servers.
                  </p>
                </div>

                {/* Address collection for ACH — shown when dealer has no address on file */}
                {!hasAddress && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium mb-3">Billing Address Required for ACH</p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Street address (optional)"
                        value={achAddressForm.street}
                        onChange={(e) => setAchAddressForm(f => ({ ...f, street: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          placeholder="City *"
                          value={achAddressForm.city}
                          onChange={(e) => setAchAddressForm(f => ({ ...f, city: e.target.value }))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                        />
                        <input
                          type="text"
                          placeholder="State *"
                          value={achAddressForm.state}
                          onChange={(e) => setAchAddressForm(f => ({ ...f, state: e.target.value }))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                        />
                        <input
                          type="text"
                          placeholder="ZIP *"
                          value={achAddressForm.zip}
                          onChange={(e) => setAchAddressForm(f => ({ ...f, zip: e.target.value }))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    By proceeding, I authorize International Shade Products Inc to debit my bank account for the amount of <strong>${helcimAchDiscountPercent > 0 ? (cartTotal * (1 - helcimAchDiscountPercent / 100)).toFixed(2) : cartTotal.toFixed(2)}</strong> on
                    or after <strong>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong> for
                    this parts order. This is a one-time electronic fund transfer. Funds may be withdrawn from your account as soon as the same day.
                  </p>
                </div>
                {helcimAchDiscountPercent > 0 && (
                  <div className="p-2 bg-green-50 rounded-lg border border-green-200 text-center">
                    <p className="text-xs text-green-700">{helcimAchDiscountPercent}% ACH discount applied — you save ${(cartTotal * helcimAchDiscountPercent / 100).toFixed(2)}</p>
                  </div>
                )}
                <button
                  onClick={handleBankAccountPayment}
                  disabled={cardProcessing}
                  className="w-full py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 bg-green-600 hover:bg-green-700"
                  title={helcimAchDiscountPercent > 0 ? `Save ${helcimAchDiscountPercent}% by paying with bank account` : undefined}
                >
                  {cardProcessing ? 'Opening secure form...' : (
                    helcimAchDiscountPercent > 0
                      ? `Pay $${(cartTotal * (1 - helcimAchDiscountPercent / 100)).toFixed(2)} via ACH (-${helcimAchDiscountPercent}%)`
                      : `Pay $${cartTotal.toFixed(2)} via ACH`
                  )}
                </button>
              </div>
            )}

            {/* Tab: Saved Methods */}
            {cardModalTab === 'saved' && (
              <div className="space-y-2 min-h-[120px]">
                {loadingSavedMethods ? (
                  <p className="text-sm text-gray-500 text-center py-8">Loading saved methods...</p>
                ) : savedMethods.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">No saved payment methods yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Pay with a new card and check "Save for future" to save it.</p>
                  </div>
                ) : (
                  savedMethods.map(method => (
                    <label
                      key={method.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedSavedMethod === method.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="savedMethod"
                          checked={selectedSavedMethod === method.id}
                          onChange={() => setSelectedSavedMethod(method.id)}
                          className="accent-blue-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {method.paymentType === 'card'
                              ? `${method.cardBrand || 'Card'} ending in ${method.cardLast4 || '????'}`
                              : `Bank ending in ${method.bankLast4 || '????'}`}
                          </span>
                          {method.cardExpMonth && (
                            <p className="text-gray-400 text-xs">exp {method.cardExpMonth}/{method.cardExpYear}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.preventDefault(); handleDeleteSavedMethod(method.id); }}
                        className="text-xs text-red-400 hover:text-red-600"
                      >Remove</button>
                    </label>
                  ))
                )}
                {savedMethods.length > 0 && (
                  <button
                    onClick={handleSavedMethodPayment}
                    disabled={cardProcessing || !selectedSavedMethod}
                    className="w-full py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 bg-green-600 hover:bg-green-700"
                  >
                    {cardProcessing ? 'Processing...' : `Pay $${cartTotal.toFixed(2)}`}
                  </button>
                )}
              </div>
            )}

            <div className="flex justify-center mt-3">
              <a
                href={`https://verify.authorize.net/anetseal/?pid=825580f2-c649-4e78-bd1f-baf21f5cfd1c&rurl=${encodeURIComponent(window.location.href)}`}
                onClick={(e) => { e.preventDefault(); window.open(e.currentTarget.href, 'AuthorizeNetVerification', 'width=600,height=430,resizable=yes,scrollbars=yes,location=yes'); }}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img src="https://verify.authorize.net/anetseal/images/secure90x72.gif" width="90" height="72" alt="Authorize.Net Merchant - Click to Verify" style={{ border: 0 }} />
              </a>
            </div>
            <p className="mt-2 text-xs text-gray-400 text-center">
              Card payments processed by Authorize.net. Bank/ACH payments processed by Helcim. Your details are never stored on our servers.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default PartsPage;
